import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  ActivityIndicator,
  TouchableOpacity,
  StyleSheet,
  Alert,
  TextInput,
  Share,
  Linking,
  Platform,
  ScrollView,
  KeyboardAvoidingView,
  Keyboard,
  TouchableWithoutFeedback,
  Modal,
  Vibration,
} from 'react-native';
import * as Location from 'expo-location';
import * as Clipboard from 'expo-clipboard';
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake';
import * as Speech from 'expo-speech';
import * as Updates from 'expo-updates';
import { useLocalSearchParams } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { io } from 'socket.io-client';

const SERVER_URL = 'https://api.ping-app.org';
const INVITE_HTTPS_BASE_URL = 'https://ping-app.org';
const APP_UI_REVISION = 'UI 20.14';
const ADDRESS_SEARCH_TIMEOUT_MS = 4500;
const BUSINESS_SEARCH_TIMEOUT_MS = 2800;
const IOS_KEYBOARD_SEARCH_FALLBACK_DELAY_MS = 1100;
const KEYBOARD_SEARCH_FALLBACK_DELAY_MS = 180;
const ROUTE_RECALCULATION_DISTANCE_METERS = 12;
const ROUTE_RECALCULATION_INTERVAL_MS = 4500;
const ROUTE_CAMERA_LOOKAHEAD_METERS = 90;
const ROUTE_CAMERA_CENTER_AHEAD_METERS = 34;
const ROUTE_CAMERA_HEADING_SMOOTHING = 0.35;
const PEDESTRIAN_ROUTE_RECALCULATION_DISTANCE_METERS = 7;
const PEDESTRIAN_ROUTE_RECALCULATION_INTERVAL_MS = 2500;
const PEDESTRIAN_CAMERA_CENTER_AHEAD_METERS = 20;
const PEDESTRIAN_CAMERA_HEADING_SMOOTHING = 0.22;
const PEDESTRIAN_ARRIVAL_DISTANCE_METERS = 30;
const PEDESTRIAN_NEAR_DESTINATION_DISTANCE_METERS = 80;
const VOICE_ROAD_NAME_MIN_DISTANCE_METERS = 120;
const PRECISE_LOCATION_OPTIONS = {
  accuracy: Location.Accuracy.BestForNavigation,
  mayShowUserSettingsDialog: true,
  timeInterval: 1000,
  distanceInterval: 1,
};
const MAX_LOCATION_ACCURACY_METERS = 45;
const LOCATION_JITTER_DISTANCE_METERS = 4;
const LOCATION_MOVING_SPEED_METERS_PER_SECOND = 1.2;
const ROUTING_SERVICES = {
  driving: [
    {
      id: 'osrm-driving',
      baseUrl: 'https://router.project-osrm.org/route/v1',
      profile: 'driving',
    },
    {
      id: 'osm-car',
      baseUrl: 'https://routing.openstreetmap.de/routed-car/route/v1',
      profile: 'driving',
    },
  ],
  foot: [
    {
      id: 'osm-foot',
      baseUrl: 'https://routing.openstreetmap.de/routed-foot/route/v1',
      profile: 'driving',
    },
    {
      id: 'osm-foot-walking',
      baseUrl: 'https://routing.openstreetmap.de/routed-foot/route/v1',
      profile: 'walking',
    },
  ],
};
const JOIN_PATH = 'join';
const LEGACY_INVITE_PATH = 'invite';
const SESSION_MODE_CONTACT = 'contact';
const SESSION_MODE_MEETING = 'meeting';
const ROUTE_TRAVEL_MODE_DRIVING = 'driving';
const ROUTE_TRAVEL_MODE_WALKING = 'foot';
const SESSION_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const SESSION_CODE_LENGTH = 6;
const KEEP_AWAKE_TAG = 'jarrive-active-session';
const SIMPLE_SIGNAL_LABELS = ['Je demarre', 'Je suis arrive'];
let nativeMapsPackage = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    nativeMapsPackage = require('react-native-maps');
  } catch (error) {
    console.log('react-native-maps indisponible dans ce binaire :', error);
  }
}

const NativeMapView = nativeMapsPackage?.default;
const NativeMarker = nativeMapsPackage?.Marker;
const NativePolyline = nativeMapsPackage?.Polyline;
const IS_NATIVE_MAPS = Platform.OS !== 'web' && !!nativeMapsPackage;
const CAN_RENDER_NATIVE_MAP = IS_NATIVE_MAPS;
const hasCoordinates = (user) =>
  typeof user?.latitude === 'number' && typeof user?.longitude === 'number';

const generateSessionCode = () =>
  Array.from({ length: SESSION_CODE_LENGTH }, () =>
    SESSION_CODE_ALPHABET[Math.floor(Math.random() * SESSION_CODE_ALPHABET.length)]
  ).join('');

const isCurrentUser = (user, currentSocketId, currentPseudo) => {
  if (currentSocketId) {
    return user.id === currentSocketId;
  }

  return user?.pseudo?.trim()?.toLowerCase() === currentPseudo.trim().toLowerCase();
};

const getDistanceScore = (from, to) => {
  const latitudeGap = from.latitude - to.latitude;
  const longitudeGap = from.longitude - to.longitude;

  return latitudeGap * latitudeGap + longitudeGap * longitudeGap;
};

const isGeneratedInvitePseudo = (value) => {
  const normalizedValue = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/\s+/g, ' ');

  return normalizedValue === "l'invite jarrive" || normalizedValue.startsWith("l'invite de ");
};

const getInvitePrefillPseudo = (currentPseudo, guestPseudo = '') => {
  const trimmedGuestPseudo = String(guestPseudo || '').trim();

  if (trimmedGuestPseudo && !isGeneratedInvitePseudo(trimmedGuestPseudo)) {
    return trimmedGuestPseudo;
  }

  const trimmedPseudo = String(currentPseudo || '').trim();

  return isGeneratedInvitePseudo(trimmedPseudo) ? '' : trimmedPseudo;
};

const getInviteRelationLabel = (user, currentPseudo = '') => {
  const invitedBy = String(user?.invitedBy || '').trim();

  if (!invitedBy) {
    return '';
  }

  const userPseudo = String(user?.pseudo || '').trim().toLowerCase();
  const inviterPseudo = invitedBy.toLowerCase();

  if (userPseudo && userPseudo === inviterPseudo) {
    return '';
  }

  const currentPseudoLabel = String(currentPseudo || '').trim();
  const inviterLabel =
    currentPseudoLabel && inviterPseudo === currentPseudoLabel.toLowerCase()
      ? 'toi'
      : invitedBy;

  return `Invite par ${inviterLabel}`;
};

const getSessionStartVoiceText = ({
  routeTarget,
  currentPseudo,
  inviteHostPseudo,
  sessionMode,
}) => {
  const targetLabel = getRouteTargetLabel(routeTarget);
  const currentPseudoLabel = String(currentPseudo || '').trim();
  const hostLabel = String(inviteHostPseudo || routeTarget?.invitedBy || '').trim();

  if (sessionMode === SESSION_MODE_MEETING) {
    if (hostLabel && hostLabel.toLowerCase() !== currentPseudoLabel.toLowerCase()) {
      return `${hostLabel} t'a donne rendez-vous. C'est parti vers le point de rendez-vous.`;
    }

    return targetLabel
      ? `C'est parti vers ${targetLabel}.`
      : "C'est parti vers le point de rendez-vous.";
  }

  if (hostLabel && hostLabel.toLowerCase() !== currentPseudoLabel.toLowerCase()) {
    return `${hostLabel} va te rejoindre.`;
  }

  return targetLabel ? `C'est parti pour rejoindre ${targetLabel}.` : '';
};

const normalizeSessionMode = (mode) => {
  const normalizedMode = String(mode || '').trim().toLowerCase();

  if (
    normalizedMode === SESSION_MODE_MEETING ||
    normalizedMode === 'rendezvous' ||
    normalizedMode === 'rendez-vous' ||
    normalizedMode === 'place' ||
    normalizedMode === 'lieu'
  ) {
    return SESSION_MODE_MEETING;
  }

  return SESSION_MODE_CONTACT;
};

const buildInviteLink = (
  sessionCode,
  hostPseudo,
  sessionMode = SESSION_MODE_CONTACT,
  guestPseudo = ''
) => {
  const trimmedCode = String(sessionCode || '').trim();
  const trimmedHostPseudo = String(hostPseudo || '').trim();
  const trimmedGuestPseudo = String(guestPseudo || '').trim();
  const queryParts = [`mode=${normalizeSessionMode(sessionMode)}`];

  if (trimmedCode) {
    queryParts.push(`code=${encodeURIComponent(trimmedCode)}`);
  }

  if (trimmedHostPseudo) {
    queryParts.push(`host=${encodeURIComponent(trimmedHostPseudo)}`);
  }

  if (trimmedGuestPseudo) {
    queryParts.push(`guest=${encodeURIComponent(trimmedGuestPseudo)}`);
  }

  const querySuffix = queryParts.length ? `?${queryParts.join('&')}` : '';

  return `${INVITE_HTTPS_BASE_URL}/${JOIN_PATH}${querySuffix}`;
};

const buildHandledInviteKey = (
  sessionCode,
  hostPseudo,
  sessionMode = SESSION_MODE_CONTACT,
  guestPseudo = ''
) =>
  `invite:${normalizeSessionMode(sessionMode)}:${String(sessionCode || '').trim()}:${String(hostPseudo || '').trim()}:${String(guestPseudo || '').trim()}`;

const parseInviteLink = (url) => {
  try {
    const parsedUrl = new URL(url);
    const normalizedPathSource =
      parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
        ? parsedUrl.pathname
        : [parsedUrl.hostname, parsedUrl.pathname].filter(Boolean).join('/');
    const normalizedPath = normalizedPathSource
      .replace(/\/+/g, '/')
      .replace(/^\/+|\/+$/g, '')
      .toLowerCase();

    const pathSegments = normalizedPath ? normalizedPath.split('/').filter(Boolean) : [];
    let inviteCode = '';

    if (
      !pathSegments.length ||
      normalizedPath === LEGACY_INVITE_PATH ||
      (pathSegments[0] === JOIN_PATH &&
        (!pathSegments[1] || pathSegments[1] === 'index.html'))
    ) {
      inviteCode = String(parsedUrl.searchParams.get('code') || '').trim();
    } else if (pathSegments[0] === JOIN_PATH && pathSegments[1]) {
      inviteCode = decodeURIComponent(pathSegments[1]).trim();
    } else {
      return null;
    }

    const inviteHost = String(parsedUrl.searchParams.get('host') || '').trim();
    const inviteGuest = String(parsedUrl.searchParams.get('guest') || '').trim();

    if (!inviteCode) {
      return null;
    }

    return {
      code: inviteCode,
      host: inviteHost,
      guest: inviteGuest,
      mode: normalizeSessionMode(parsedUrl.searchParams.get('mode')),
      autoJoin: String(parsedUrl.searchParams.get('autojoin') || '').trim() === '1',
    };
  } catch (error) {
    console.log("Lien d'invitation invalide :", error);
    return null;
  }
};

const getRouteTargetLabel = (target) =>
  String(target?.label || target?.pseudo || 'destination').trim();

const buildMeetingDestination = (coordinate, label = 'Point de rendez-vous', description = '') => {
  const latitude = Number(coordinate?.latitude);
  const longitude = Number(coordinate?.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const destinationLabel = String(label || coordinate?.label || 'Point de rendez-vous').trim();
  const destinationDescription = String(description || coordinate?.description || '').trim();

  return {
    id: `meeting:${latitude.toFixed(6)}:${longitude.toFixed(6)}`,
    label: destinationLabel,
    description: destinationDescription,
    latitude,
    longitude,
    category: 'Rendez-vous',
    type: 'Carte',
  };
};

const getAddressSuggestionTypeLabel = (suggestion) => {
  const suggestionClass = String(suggestion?.class || '').toLowerCase();
  const suggestionType = String(suggestion?.type || '').toLowerCase();

  if (
    suggestionClass === 'shop' ||
    ['restaurant', 'cafe', 'bar', 'fast_food', 'pub'].includes(suggestionType)
  ) {
    return 'Commerce';
  }

  if (
    suggestionClass === 'office' ||
    ['company', 'industrial', 'commercial'].includes(suggestionType)
  ) {
    return 'Entreprise';
  }

  if (suggestionClass === 'amenity' || suggestionClass === 'tourism') {
    return 'Lieu';
  }

  if (suggestionClass === 'highway' || suggestionClass === 'place') {
    return 'Adresse';
  }

  return 'Adresse';
};

const buildAddressSuggestionLabel = (suggestion) => {
  const namedLabel = String(
    suggestion?.namedetails?.name ||
      suggestion?.name ||
      suggestion?.address?.amenity ||
      suggestion?.address?.shop ||
      suggestion?.address?.office ||
      ''
  ).trim();
  const displayName = String(suggestion?.display_name || '').trim();

  return namedLabel || displayName || 'Adresse';
};

const normalizeAddressSuggestion = (suggestion, origin) => {
  const latitude = Number(suggestion?.lat);
  const longitude = Number(suggestion?.lon);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const coordinate = { latitude, longitude };
  const distanceMeters = origin
    ? getDistanceBetweenCoordinates(origin, coordinate)
    : null;
  const typeLabel = getAddressSuggestionTypeLabel(suggestion);

  return {
    id: String(suggestion?.place_id || `${latitude}-${longitude}`),
    label: buildAddressSuggestionLabel(suggestion),
    description: String(suggestion?.display_name || '').trim(),
    typeLabel,
    latitude,
    longitude,
    distanceMeters,
    distanceLabel: formatDistance(distanceMeters),
    sourcePriority:
      typeLabel === 'Commerce' || typeLabel === 'Entreprise' ? 0.5 : 2,
  };
};

const getFrenchAddressFeatureTypeLabel = (feature) => {
  const featureType = String(feature?.properties?.type || '').toLowerCase();

  if (featureType === 'housenumber') {
    return 'Numero';
  }

  if (featureType === 'street') {
    return 'Rue';
  }

  if (featureType === 'municipality') {
    return 'Ville';
  }

  return 'Adresse';
};

const normalizeFrenchAddressFeature = (feature, origin) => {
  const coordinates = feature?.geometry?.coordinates;
  const longitude = Number(coordinates?.[0]);
  const latitude = Number(coordinates?.[1]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const coordinate = { latitude, longitude };
  const distanceMeters = origin
    ? getDistanceBetweenCoordinates(origin, coordinate)
    : null;
  const properties = feature?.properties || {};
  const label = String(properties.label || properties.name || 'Adresse').trim();
  const description = [properties.postcode, properties.city, properties.context]
    .filter(Boolean)
    .join(' - ');

  return {
    id: String(properties.id || `ban:${latitude}-${longitude}`),
    label,
    description,
    typeLabel: getFrenchAddressFeatureTypeLabel(feature),
    latitude,
    longitude,
    distanceMeters,
    distanceLabel: formatDistance(distanceMeters),
    sourcePriority: properties.type === 'housenumber' ? 0 : 1,
  };
};

const buildFrenchAddressSearchUrl = (
  query,
  origin,
  { limit = 10, type = '' } = {}
) => {
  const queryParts = [
    `q=${encodeURIComponent(query)}`,
    `limit=${limit}`,
    'autocomplete=1',
  ];

  if (type) {
    queryParts.push(`type=${encodeURIComponent(type)}`);
  }

  if (origin) {
    queryParts.push(
      `lat=${encodeURIComponent(origin.latitude)}`,
      `lon=${encodeURIComponent(origin.longitude)}`
    );
  }

  return `https://api-adresse.data.gouv.fr/search/?${queryParts.join('&')}`;
};

const fetchJsonWithTimeout = async (
  url,
  { timeoutMs = ADDRESS_SEARCH_TIMEOUT_MS, errorLabel = 'HTTP', ...options } = {}
) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`${errorLabel} ${response.status}`);
    }

    return response.json();
  } finally {
    clearTimeout(timeout);
  }
};

const fetchFrenchAddressPayload = async (query, origin, options = {}) => {
  return fetchJsonWithTimeout(buildFrenchAddressSearchUrl(query, origin, options), {
    timeoutMs: ADDRESS_SEARCH_TIMEOUT_MS,
    errorLabel: 'French address HTTP',
    headers: {
      Accept: 'application/json',
    },
  });
};

const normalizeFrenchAddressPayload = (payload, origin, sourcePriorityOverride = null) =>
  (Array.isArray(payload?.features) ? payload.features : [])
    .map((feature) => {
      const suggestion = normalizeFrenchAddressFeature(feature, origin);

      return suggestion && typeof sourcePriorityOverride === 'number'
        ? { ...suggestion, sourcePriority: sourcePriorityOverride }
        : suggestion;
    })
    .filter(Boolean);

const mergeAddressSuggestions = (suggestionGroups) => {
  const mergedSuggestions = [];
  const seenCoordinates = new Set();

  suggestionGroups.flat().forEach((suggestion) => {
    if (!suggestion) {
      return;
    }

    const coordinateKey = `${Number(suggestion.latitude).toFixed(5)}:${Number(
      suggestion.longitude
    ).toFixed(5)}`;

    if (seenCoordinates.has(coordinateKey)) {
      return;
    }

    seenCoordinates.add(coordinateKey);
    mergedSuggestions.push(suggestion);
  });

  return mergedSuggestions
    .sort((firstSuggestion, secondSuggestion) => {
      if (firstSuggestion.sourcePriority !== secondSuggestion.sourcePriority) {
        return (firstSuggestion.sourcePriority ?? 2) - (secondSuggestion.sourcePriority ?? 2);
      }

      if (
        typeof firstSuggestion.distanceMeters === 'number' &&
        typeof secondSuggestion.distanceMeters === 'number'
      ) {
        return firstSuggestion.distanceMeters - secondSuggestion.distanceMeters;
      }

      return 0;
    })
    .slice(0, 8);
};

const getClosestUser = (users, currentLocation, currentSocketId, currentPseudo) => {
  const candidates = users.filter(
    (user) =>
      hasCoordinates(user) && !isCurrentUser(user, currentSocketId, currentPseudo)
  );

  if (!candidates.length) {
    return null;
  }

  return candidates.reduce((closestUser, user) => {
    if (!closestUser) {
      return user;
    }

    return getDistanceScore(currentLocation, user) <
      getDistanceScore(currentLocation, closestUser)
      ? user
      : closestUser;
  }, null);
};

const formatDistance = (distanceMeters) => {
  if (typeof distanceMeters !== 'number' || Number.isNaN(distanceMeters)) {
    return null;
  }

  if (distanceMeters < 1000) {
    return `${Math.round(distanceMeters)} m`;
  }

  return `${(distanceMeters / 1000).toFixed(1)} km`;
};

const formatDuration = (durationSeconds) => {
  if (typeof durationSeconds !== 'number' || Number.isNaN(durationSeconds)) {
    return null;
  }

  const roundedMinutes = Math.max(1, Math.round(durationSeconds / 60));

  if (roundedMinutes < 60) {
    return `${roundedMinutes} min`;
  }

  const hours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;

  if (!minutes) {
    return `${hours} h`;
  }

  return `${hours} h ${minutes} min`;
};

const formatArrivalTime = (durationSeconds) => {
  if (typeof durationSeconds !== 'number' || Number.isNaN(durationSeconds)) {
    return null;
  }

  const arrivalDate = new Date(Date.now() + durationSeconds * 1000);

  return arrivalDate.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toRadians = (value) => (value * Math.PI) / 180;

const getDistanceBetweenCoordinates = (from, to) => {
  if (!from || !to) {
    return null;
  }

  const earthRadius = 6371000;
  const latitudeDelta = toRadians(to.latitude - from.latitude);
  const longitudeDelta = toRadians(to.longitude - from.longitude);
  const startLatitude = toRadians(from.latitude);
  const endLatitude = toRadians(to.latitude);

  const a =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(startLatitude) *
      Math.cos(endLatitude) *
      Math.sin(longitudeDelta / 2) *
      Math.sin(longitudeDelta / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return earthRadius * c;
};

const getLocationAccuracy = (coordinate) =>
  typeof coordinate?.accuracy === 'number' && coordinate.accuracy >= 0
    ? coordinate.accuracy
    : null;

const getLocationSpeed = (coordinate) =>
  typeof coordinate?.speed === 'number' && coordinate.speed >= 0
    ? coordinate.speed
    : null;

const normalizeLocationCoords = (locationSource) => {
  const coords = locationSource?.coords || locationSource;
  const latitude = Number(coords?.latitude);
  const longitude = Number(coords?.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return {
    ...coords,
    latitude,
    longitude,
    timestamp: locationSource?.timestamp || coords?.timestamp || Date.now(),
  };
};

const shouldUseLocationUpdate = (nextLocation, previousLocation) => {
  if (!nextLocation) {
    return false;
  }

  if (!previousLocation) {
    return true;
  }

  const distance = getDistanceBetweenCoordinates(previousLocation, nextLocation);
  const nextAccuracy = getLocationAccuracy(nextLocation);
  const previousAccuracy = getLocationAccuracy(previousLocation);

  if (
    typeof distance === 'number' &&
    typeof nextAccuracy === 'number' &&
    nextAccuracy > MAX_LOCATION_ACCURACY_METERS &&
    (typeof previousAccuracy !== 'number' || previousAccuracy <= nextAccuracy) &&
    distance < nextAccuracy
  ) {
    return false;
  }

  if (typeof distance === 'number' && distance < LOCATION_JITTER_DISTANCE_METERS) {
    const speed = getLocationSpeed(nextLocation);
    const isMoving = typeof speed === 'number' && speed >= LOCATION_MOVING_SPEED_METERS_PER_SECOND;

    if (
      !isMoving &&
      (typeof nextAccuracy !== 'number' ||
        typeof previousAccuracy !== 'number' ||
        nextAccuracy >= previousAccuracy - 1)
    ) {
      return false;
    }
  }

  return true;
};

const getPreciseCurrentPositionAsync = () =>
  Location.getCurrentPositionAsync(PRECISE_LOCATION_OPTIONS);

const getRemainingDistanceAlongRoute = (currentLocation, routeCoordinates) => {
  if (
    !currentLocation ||
    !Array.isArray(routeCoordinates) ||
    routeCoordinates.length < 2
  ) {
    return null;
  }

  let nearestIndex = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;

  routeCoordinates.forEach((coordinate, index) => {
    const distance = getDistanceBetweenCoordinates(currentLocation, coordinate);

    if (typeof distance === 'number' && distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  });

  let remainingDistance = nearestDistance;

  for (let index = nearestIndex; index < routeCoordinates.length - 1; index += 1) {
    const segmentDistance = getDistanceBetweenCoordinates(
      routeCoordinates[index],
      routeCoordinates[index + 1]
    );

    if (typeof segmentDistance === 'number') {
      remainingDistance += segmentDistance;
    }
  }

  return remainingDistance;
};

const buildRoutingUrl = (routingService, from, to) => {
  const coordinates = `${from.longitude},${from.latitude};${to.longitude},${to.latitude}`;
  const query = [
    'overview=full',
    'geometries=geojson',
    'alternatives=false',
    'steps=true',
    'continue_straight=false',
    'radiuses=100;100',
  ].join('&');

  return `${routingService.baseUrl}/${routingService.profile}/${coordinates}?${query}`;
};

const fetchRouteForProfile = async (profile, routingService, from, to) => {
  const response = await fetch(buildRoutingUrl(routingService, from, to), {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Routing ${profile}/${routingService.id} HTTP ${response.status}`);
  }

  const data = await response.json();
  const route = data?.routes?.[0];
  const coordinates = route?.geometry?.coordinates;

  if (!Array.isArray(coordinates) || !coordinates.length) {
    throw new Error(`Aucun trajet ${profile}/${routingService.id} trouve`);
  }

  const normalizedCoordinates = coordinates
    .map(([longitude, latitude]) => ({
      latitude: Number(latitude),
      longitude: Number(longitude),
    }))
    .filter(
      (coordinate) =>
        Number.isFinite(coordinate.latitude) && Number.isFinite(coordinate.longitude)
    );

  if (!normalizedCoordinates.length) {
    throw new Error(`Coordonnees ${profile}/${routingService.id} invalides`);
  }

  return {
    profile,
    serviceId: routingService.id,
    route,
    coordinates: normalizedCoordinates,
  };
};

const getBearingBetweenCoordinates = (from, to) => {
  if (!from || !to) {
    return 0;
  }

  const startLatitude = toRadians(from.latitude);
  const endLatitude = toRadians(to.latitude);
  const longitudeDelta = toRadians(to.longitude - from.longitude);
  const y = Math.sin(longitudeDelta) * Math.cos(endLatitude);
  const x =
    Math.cos(startLatitude) * Math.sin(endLatitude) -
    Math.sin(startLatitude) * Math.cos(endLatitude) * Math.cos(longitudeDelta);
  const bearing = (Math.atan2(y, x) * 180) / Math.PI;

  return (bearing + 360) % 360;
};

const getCoordinateAtBearingDistance = (from, bearing, distanceMeters) => {
  if (
    !from ||
    typeof bearing !== 'number' ||
    typeof distanceMeters !== 'number' ||
    !Number.isFinite(from.latitude) ||
    !Number.isFinite(from.longitude) ||
    !Number.isFinite(bearing) ||
    !Number.isFinite(distanceMeters) ||
    distanceMeters <= 0
  ) {
    return from;
  }

  const earthRadius = 6371000;
  const angularDistance = distanceMeters / earthRadius;
  const bearingRadians = toRadians(bearing);
  const latitudeRadians = toRadians(from.latitude);
  const longitudeRadians = toRadians(from.longitude);

  const destinationLatitude = Math.asin(
    Math.sin(latitudeRadians) * Math.cos(angularDistance) +
      Math.cos(latitudeRadians) *
        Math.sin(angularDistance) *
        Math.cos(bearingRadians)
  );
  const destinationLongitude =
    longitudeRadians +
    Math.atan2(
      Math.sin(bearingRadians) *
        Math.sin(angularDistance) *
        Math.cos(latitudeRadians),
      Math.cos(angularDistance) -
        Math.sin(latitudeRadians) * Math.sin(destinationLatitude)
    );

  return {
    latitude: (destinationLatitude * 180) / Math.PI,
    longitude: (destinationLongitude * 180) / Math.PI,
  };
};

const getShortestHeadingDelta = (fromHeading, toHeading) =>
  ((toHeading - fromHeading + 540) % 360) - 180;

const smoothHeading = (
  previousHeading,
  nextHeading,
  smoothing = ROUTE_CAMERA_HEADING_SMOOTHING
) => {
  if (typeof previousHeading !== 'number') {
    return nextHeading;
  }

  const headingDelta = getShortestHeadingDelta(previousHeading, nextHeading);

  return (previousHeading + headingDelta * smoothing + 360) % 360;
};

const normalizeDeviceHeading = (heading) => {
  const trueHeading = Number(heading?.trueHeading);
  const magneticHeading = Number(heading?.magHeading);
  const nextHeading =
    Number.isFinite(trueHeading) && trueHeading >= 0 ? trueHeading : magneticHeading;

  if (!Number.isFinite(nextHeading) || nextHeading < 0) {
    return null;
  }

  return (nextHeading + 360) % 360;
};

const getRelativeDirectionLabel = (headingDelta) => {
  if (typeof headingDelta !== 'number' || Number.isNaN(headingDelta)) {
    return 'oriente le telephone';
  }

  const absoluteDelta = Math.abs(headingDelta);

  if (absoluteDelta <= 20) {
    return 'devant toi';
  }

  if (absoluteDelta <= 70) {
    return headingDelta > 0 ? 'devant a droite' : 'devant a gauche';
  }

  if (absoluteDelta <= 120) {
    return headingDelta > 0 ? 'sur ta droite' : 'sur ta gauche';
  }

  if (absoluteDelta <= 160) {
    return headingDelta > 0 ? 'derriere a droite' : 'derriere a gauche';
  }

  return 'derriere toi';
};

const getCameraSettingsForTravelMode = (travelMode, hasHeadingTarget) => {
  if (travelMode === ROUTE_TRAVEL_MODE_WALKING) {
    return {
      pitch: hasHeadingTarget ? 58 : 0,
      zoom: hasHeadingTarget ? 20.55 : 18.2,
      centerAheadMeters: hasHeadingTarget ? PEDESTRIAN_CAMERA_CENTER_AHEAD_METERS : 0,
      smoothing: PEDESTRIAN_CAMERA_HEADING_SMOOTHING,
    };
  }

  return {
    pitch: hasHeadingTarget ? 68 : 0,
    zoom: hasHeadingTarget ? 20.05 : 16.5,
    centerAheadMeters: hasHeadingTarget ? ROUTE_CAMERA_CENTER_AHEAD_METERS : 0,
    smoothing: ROUTE_CAMERA_HEADING_SMOOTHING,
  };
};

const getRouteHeadingTarget = (currentLocation, routeCoordinates, fallbackTarget) => {
  if (!currentLocation) {
    return fallbackTarget || null;
  }

  if (Array.isArray(routeCoordinates) && routeCoordinates.length > 1) {
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    routeCoordinates.forEach((coordinate, index) => {
      const distance = getDistanceBetweenCoordinates(currentLocation, coordinate);

      if (typeof distance === 'number' && distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    let lookaheadDistance = 0;

    for (let index = nearestIndex; index < routeCoordinates.length - 1; index += 1) {
      const segmentDistance = getDistanceBetweenCoordinates(
        routeCoordinates[index],
        routeCoordinates[index + 1]
      );

      if (typeof segmentDistance === 'number') {
        lookaheadDistance += segmentDistance;
      }

      if (lookaheadDistance >= ROUTE_CAMERA_LOOKAHEAD_METERS) {
        return routeCoordinates[index + 1];
      }
    }

    return routeCoordinates[Math.min(nearestIndex + 1, routeCoordinates.length - 1)];
  }

  return fallbackTarget || null;
};

const normalizeManeuverValue = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');

const getRoadLabel = (step) => {
  const roadName = String(step?.name || '').trim();
  const roadRef = String(step?.ref || '').trim();
  const rotaryName = String(step?.rotary_name || '').trim();

  return roadName || rotaryName || roadRef;
};

const shouldSpeakRoadLabel = (step) => {
  const roadLabel = getRoadLabel(step);

  if (!roadLabel) {
    return false;
  }

  const maneuverType = normalizeManeuverValue(step?.maneuver?.type);
  const stepDistance = Number(step?.distance) || 0;

  if (stepDistance < VOICE_ROAD_NAME_MIN_DISTANCE_METERS) {
    return false;
  }

  return ![
    'roundabout',
    'rotary',
    'roundabout turn',
    'exit roundabout',
    'exit rotary',
    'on ramp',
    'off ramp',
    'fork',
    'end of road',
    'notification',
    'new name',
  ].includes(maneuverType);
};

const getFrenchModifierText = (modifier) => {
  switch (normalizeManeuverValue(modifier)) {
    case 'uturn':
      return 'faites demi-tour';
    case 'sharp right':
      return 'tournez franchement a droite';
    case 'right':
      return 'tournez a droite';
    case 'slight right':
      return 'tournez legerement a droite';
    case 'straight':
      return 'continuez tout droit';
    case 'slight left':
      return 'tournez legerement a gauche';
    case 'left':
      return 'tournez a gauche';
    case 'sharp left':
      return 'tournez franchement a gauche';
    default:
      return 'continuez';
  }
};

const getFrenchPedestrianModifierText = (modifier) => {
  switch (normalizeManeuverValue(modifier)) {
    case 'uturn':
      return 'fais demi-tour';
    case 'sharp right':
      return 'tourne franchement a droite';
    case 'right':
      return 'tourne a droite';
    case 'slight right':
      return 'prends legerement a droite';
    case 'straight':
      return 'continue tout droit';
    case 'slight left':
      return 'prends legerement a gauche';
    case 'left':
      return 'tourne a gauche';
    case 'sharp left':
      return 'tourne franchement a gauche';
    default:
      return 'continue';
  }
};

const getFrenchLaneText = (modifier) => {
  switch (normalizeManeuverValue(modifier)) {
    case 'slight left':
    case 'left':
    case 'sharp left':
      return 'restez sur la gauche';
    case 'slight right':
    case 'right':
    case 'sharp right':
      return 'restez sur la droite';
    default:
      return 'restez dans la voie actuelle';
  }
};

const getFrenchMergeText = (modifier) => {
  switch (normalizeManeuverValue(modifier)) {
    case 'left':
    case 'slight left':
    case 'sharp left':
      return 'inserez-vous par la gauche';
    case 'right':
    case 'slight right':
    case 'sharp right':
      return 'inserez-vous par la droite';
    default:
      return 'inserez-vous';
  }
};

const getExitLabel = (exitNumber) => {
  if (exitNumber === 1) {
    return '1re';
  }

  return `${exitNumber}e`;
};

const buildRouteInstruction = (
  step,
  targetLabel,
  isMeetingTarget = false,
  options = {}
) => {
  const includeRoadLabel = options.includeRoadLabel !== false;
  const maneuverType = normalizeManeuverValue(step?.maneuver?.type);
  const modifier = normalizeManeuverValue(step?.maneuver?.modifier);
  const roadLabel = includeRoadLabel ? getRoadLabel(step) : '';
  const roadSuffix = roadLabel ? ` sur ${roadLabel}` : '';
  const exitNumber = Number(step?.maneuver?.exit);

  switch (maneuverType) {
    case 'depart':
      return roadLabel
        ? `Demarrez sur ${roadLabel}`
        : 'Demarrez le trajet';
    case 'arrive':
      if (!targetLabel) {
        return 'Vous etes arrive';
      }

      return isMeetingTarget
        ? `Vous etes arrive au point de rendez-vous`
        : `Vous etes arrive chez ${targetLabel}`;
    case 'turn':
    case 'continue':
    case 'new name':
    case 'notification':
    case 'roundabout turn':
      return `${getFrenchModifierText(modifier)}${roadSuffix}`;
    case 'merge':
      return `${getFrenchMergeText(modifier)}${roadSuffix}`;
    case 'on ramp':
      return roadLabel
        ? `prenez la bretelle vers ${roadLabel}`
        : 'prenez la bretelle d acces';
    case 'off ramp':
      return roadLabel
        ? `prenez la sortie vers ${roadLabel}`
        : 'prenez la sortie';
    case 'fork':
      return `${getFrenchLaneText(modifier)}${roadSuffix}`;
    case 'end of road':
      return `au bout de la route, ${getFrenchModifierText(modifier)}${roadSuffix}`;
    case 'roundabout':
    case 'rotary':
    case 'exit roundabout':
    case 'exit rotary':
      if (Number.isFinite(exitNumber) && exitNumber > 0) {
        return roadLabel
          ? `au rond-point, prenez la ${getExitLabel(exitNumber)} sortie vers ${roadLabel}`
          : `au rond-point, prenez la ${getExitLabel(exitNumber)} sortie`;
      }

      return roadLabel
        ? `entrez dans le rond-point vers ${roadLabel}`
        : 'entrez dans le rond-point';
    default:
      return roadLabel ? `continuez sur ${roadLabel}` : 'continuez';
  }
};

const buildPedestrianRouteInstruction = (
  step,
  targetLabel,
  isMeetingTarget = false,
  options = {}
) => {
  const includeRoadLabel = options.includeRoadLabel !== false;
  const maneuverType = normalizeManeuverValue(step?.maneuver?.type);
  const modifier = normalizeManeuverValue(step?.maneuver?.modifier);
  const roadLabel = includeRoadLabel ? getRoadLabel(step) : '';
  const roadSuffix = roadLabel ? ` vers ${roadLabel}` : '';
  const exitNumber = Number(step?.maneuver?.exit);

  switch (maneuverType) {
    case 'depart':
      return roadLabel
        ? `Commence a marcher vers ${roadLabel}`
        : 'Commence le trajet a pied';
    case 'arrive':
      if (!targetLabel) {
        return 'Tu es arrive';
      }

      return isMeetingTarget
        ? 'Tu es arrive au point de rendez-vous'
        : `Tu es arrive pres de ${targetLabel}`;
    case 'turn':
    case 'continue':
    case 'new name':
    case 'notification':
    case 'roundabout turn':
      return `${getFrenchPedestrianModifierText(modifier)}${roadSuffix}`;
    case 'merge':
      return `${getFrenchPedestrianModifierText(modifier)}${roadSuffix}`;
    case 'on ramp':
    case 'off ramp':
      return roadLabel ? `prends le passage vers ${roadLabel}` : 'prends le passage';
    case 'fork':
      return `${getFrenchLaneText(modifier).replace('restez', 'reste')}${roadSuffix}`;
    case 'end of road':
      return `au bout du chemin, ${getFrenchPedestrianModifierText(modifier)}${roadSuffix}`;
    case 'roundabout':
    case 'rotary':
    case 'exit roundabout':
    case 'exit rotary':
      if (Number.isFinite(exitNumber) && exitNumber > 0) {
        return roadLabel
          ? `au rond-point, prends la ${getExitLabel(exitNumber)} sortie vers ${roadLabel}`
          : `au rond-point, prends la ${getExitLabel(exitNumber)} sortie`;
      }

      return roadLabel
        ? `entre dans le rond-point vers ${roadLabel}`
        : 'entre dans le rond-point';
    default:
      return roadLabel ? `continue vers ${roadLabel}` : 'continue';
  }
};

const buildRouteVoiceInstruction = (step, targetLabel, isMeetingTarget = false) =>
  buildRouteInstruction(step, targetLabel, isMeetingTarget, {
    includeRoadLabel: shouldSpeakRoadLabel(step),
  });

const buildPedestrianRouteVoiceInstruction = (step, targetLabel, isMeetingTarget = false) =>
  buildPedestrianRouteInstruction(step, targetLabel, isMeetingTarget, {
    includeRoadLabel: shouldSpeakRoadLabel(step),
  });

const getVoiceTriggerDistance = (step, travelMode = ROUTE_TRAVEL_MODE_DRIVING) => {
  const maneuverType = normalizeManeuverValue(step?.maneuverType);
  const stepDistance = typeof step?.distance === 'number' ? step.distance : 0;
  const isWalking = travelMode === ROUTE_TRAVEL_MODE_WALKING;

  if (maneuverType === 'arrive') {
    return isWalking ? 45 : 35;
  }

  if (maneuverType === 'depart') {
    return isWalking ? 15 : 20;
  }

  if (isWalking) {
    if (stepDistance >= 500) {
      return 95;
    }

    if (stepDistance >= 200) {
      return 65;
    }

    if (stepDistance >= 80) {
      return 40;
    }

    return 24;
  }

  if (stepDistance >= 1000) {
    return 220;
  }

  if (stepDistance >= 500) {
    return 150;
  }

  if (stepDistance >= 200) {
    return 90;
  }

  if (stepDistance >= 80) {
    return 55;
  }

  return 30;
};

const buildRouteSteps = (route, targetUser, travelMode = ROUTE_TRAVEL_MODE_DRIVING) => {
  const targetLabel = getRouteTargetLabel(targetUser);
  const isMeetingTarget = targetUser?.category === 'Rendez-vous';
  const targetId = targetUser?.id || 'target';
  const legs = Array.isArray(route?.legs) ? route.legs : [];
  const isWalking = travelMode === ROUTE_TRAVEL_MODE_WALKING;

  return legs.flatMap((leg, legIndex) =>
    (Array.isArray(leg?.steps) ? leg.steps : []).map((step, stepIndex) => {
      const location = {
        latitude: Number(step?.maneuver?.location?.[1]),
        longitude: Number(step?.maneuver?.location?.[0]),
      };
      const maneuverType = normalizeManeuverValue(step?.maneuver?.type);
      const instruction = isWalking
        ? buildPedestrianRouteInstruction(step, targetLabel, isMeetingTarget)
        : buildRouteInstruction(step, targetLabel, isMeetingTarget);
      const voiceInstruction = isWalking
        ? buildPedestrianRouteVoiceInstruction(step, targetLabel, isMeetingTarget)
        : buildRouteVoiceInstruction(step, targetLabel, isMeetingTarget);
      const roundedLatitude = Number.isFinite(location.latitude)
        ? location.latitude.toFixed(4)
        : '0';
      const roundedLongitude = Number.isFinite(location.longitude)
        ? location.longitude.toFixed(4)
        : '0';

      let voiceKey = `${targetId}-${legIndex}-${stepIndex}-${roundedLatitude}-${roundedLongitude}`;

      if (maneuverType === 'depart') {
        voiceKey = `${targetId}-depart`;
      }

      if (maneuverType === 'arrive') {
        voiceKey = `${targetId}-arrive`;
      }

      return {
        id: `${targetId}-${legIndex}-${stepIndex}`,
        voiceKey,
        instruction,
        voiceText: voiceInstruction.charAt(0).toUpperCase() + voiceInstruction.slice(1),
        distance: Number(step?.distance) || 0,
        duration: Number(step?.duration) || 0,
        distanceLabel: formatDistance(Number(step?.distance) || 0),
        durationLabel: formatDuration(Number(step?.duration) || 0),
        roadLabel: getRoadLabel(step),
        location,
        maneuverType,
        travelMode,
      };
    })
  );
};

const upsertActivityItem = (activityList, activityItem) => {
  const nextActivity = [
    activityItem,
    ...activityList.filter((item) => item.id !== activityItem.id),
  ];

  return nextActivity.slice(0, 8);
};

export default function App() {
  const insets = useSafeAreaInsets();
  const inviteParams = useLocalSearchParams<{
    code?: string;
    host?: string;
    guest?: string;
    mode?: string;
    autoJoin?: string;
    autojoin?: string;
  }>();
  const [pseudo, setPseudo] = useState('');
  const [code, setCode] = useState('');
  const [phone, setPhone] = useState('');
  const [sessionMode, setSessionMode] = useState(SESSION_MODE_CONTACT);
  const [meetingAddressQuery, setMeetingAddressQuery] = useState('');
  const [meetingAddressSubmittedQuery, setMeetingAddressSubmittedQuery] = useState('');
  const [meetingAddressSearchNonce, setMeetingAddressSearchNonce] = useState(0);
  const [meetingAddressSuggestions, setMeetingAddressSuggestions] = useState([]);
  const [meetingAddressLoading, setMeetingAddressLoading] = useState(false);
  const [meetingAddressError, setMeetingAddressError] = useState('');
  const [meetingAddressSearchActive, setMeetingAddressSearchActive] = useState(false);
  const [meetingAddressLastSearchedQuery, setMeetingAddressLastSearchedQuery] =
    useState('');
  const [meetingSearchOrigin, setMeetingSearchOrigin] = useState(null);
  const [addressSearchModalVisible, setAddressSearchModalVisible] = useState(false);
  const [invitePromptVisible, setInvitePromptVisible] = useState(false);
  const [inviteGuestPseudo, setInviteGuestPseudo] = useState('');
  const [inviteHostPseudo, setInviteHostPseudo] = useState('');
  const [invitePromptError, setInvitePromptError] = useState('');
  const [location, setLocation] = useState(null);
  const [joined, setJoined] = useState(false);
  const [roomUsers, setRoomUsers] = useState([]);
  const [roomDestination, setRoomDestination] = useState(null);
  const [socketId, setSocketId] = useState('');
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [routeTarget, setRouteTarget] = useState(null);
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeDuration, setRouteDuration] = useState(null);
  const [routeSteps, setRouteSteps] = useState([]);
  const [routeIsFallback, setRouteIsFallback] = useState(false);
  const [routeStartDistance, setRouteStartDistance] = useState(null);
  const [routeStartTargetId, setRouteStartTargetId] = useState('');
  const [spokenStepKeys, setSpokenStepKeys] = useState([]);
  const [, setActivityHistory] = useState([]);
  const [incomingSignal, setIncomingSignal] = useState(null);
  const [sessionConnectionMessage, setSessionConnectionMessage] = useState('');
  const [socketOnline, setSocketOnline] = useState(false);
  const [socketError, setSocketError] = useState('');
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [mapFollowEnabled, setMapFollowEnabled] = useState(false);
  const [routeTravelMode, setRouteTravelMode] = useState(ROUTE_TRAVEL_MODE_DRIVING);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [deviceHeading, setDeviceHeading] = useState(null);

  const mapRef = useRef(null);
  const meetingAddressInputRef = useRef(null);
  const socketRef = useRef(null);
  const watchRef = useRef(null);
  const headingWatchRef = useRef(null);
  const routeRequestRef = useRef({ targetKey: '', origin: null, requestedAt: 0 });
  const routeDurationPerMeterRef = useRef(null);
  const cameraAnimationTimeoutRef = useRef(null);
  const cameraHeadingRef = useRef(null);
  const isCameraAnimatingRef = useRef(false);
  const pseudoRef = useRef('');
  const spokenStepKeysRef = useRef(new Set());
  const lastRouteSpeechRef = useRef({ voiceKey: '', spokenAt: 0 });
  const routeVoiceTargetIdRef = useRef('');
  const sessionStartVoiceKeyRef = useRef('');
  const handledInviteUrlsRef = useRef(new Set());
  const activeJoinKeyRef = useRef('');
  const inviteJoinTimerRef = useRef(null);
  const pendingJoinRef = useRef(null);
  const locationRef = useRef(null);
  const latestLocationRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  const selectedMeetingAddressQueryRef = useRef('');
  const pendingMeetingAddressSearchRef = useRef('');
  const pendingMeetingAddressSearchTimerRef = useRef(null);
  const addressResultsKeyboardDismissKeyRef = useRef('');
  const meetingOriginRequestRef = useRef(false);
  const walkingNearDestinationVibrationRef = useRef('');

  const startSubmittedMeetingAddressSearch = React.useCallback((query) => {
    const trimmedQuery = String(query || '').trim();

    if (trimmedQuery.length < 3) {
      return;
    }

    if (pendingMeetingAddressSearchTimerRef.current) {
      clearTimeout(pendingMeetingAddressSearchTimerRef.current);
      pendingMeetingAddressSearchTimerRef.current = null;
    }

    pendingMeetingAddressSearchRef.current = '';
    addressResultsKeyboardDismissKeyRef.current = '';
    setMeetingAddressSubmittedQuery(trimmedQuery);
    setMeetingAddressLastSearchedQuery('');
    setMeetingAddressSuggestions([]);
    setMeetingAddressError('');
    setMeetingAddressLoading(true);
    setMeetingAddressSearchActive(true);
    setMeetingAddressSearchNonce((currentValue) => currentValue + 1);
  }, []);

  useEffect(() => {
    pseudoRef.current = pseudo.trim();
  }, [pseudo]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (!joined || routeTravelMode !== ROUTE_TRAVEL_MODE_WALKING) {
      if (headingWatchRef.current) {
        headingWatchRef.current.remove();
        headingWatchRef.current = null;
      }

      setDeviceHeading(null);
      return;
    }

    let isActive = true;

    Location.watchHeadingAsync((heading) => {
      const normalizedHeading = normalizeDeviceHeading(heading);

      if (isActive && typeof normalizedHeading === 'number') {
        setDeviceHeading(normalizedHeading);
      }
    })
      .then((subscription) => {
        if (!isActive) {
          subscription.remove();
          return;
        }

        headingWatchRef.current = subscription;
      })
      .catch((error) => {
        console.log('Erreur boussole pieton :', error);
      });

    return () => {
      isActive = false;

      if (headingWatchRef.current) {
        headingWatchRef.current.remove();
        headingWatchRef.current = null;
      }
    };
  }, [joined, routeTravelMode]);

  const resetSpokenSteps = React.useCallback(() => {
    spokenStepKeysRef.current = new Set();
    setSpokenStepKeys([]);
  }, []);

  const speakSessionStart = React.useCallback(async (message, voiceKey) => {
    if (!message || !voiceKey || sessionStartVoiceKeyRef.current === voiceKey) {
      return;
    }

    sessionStartVoiceKeyRef.current = voiceKey;

    try {
      await Speech.stop();
    } catch (error) {
      console.log('Erreur arret voix depart :', error);
    }

    Speech.speak(message, {
      language: 'fr-FR',
      pitch: 1,
      rate: 0.96,
    });
  }, []);

  const speakRouteStep = React.useCallback(async (step, prefix = '') => {
    if (!step?.voiceText) {
      return;
    }

    const now = Date.now();

    if (
      lastRouteSpeechRef.current.voiceKey === step.voiceKey &&
      now - lastRouteSpeechRef.current.spokenAt < 2500
    ) {
      return;
    }

    lastRouteSpeechRef.current = {
      voiceKey: step.voiceKey,
      spokenAt: now,
    };

    try {
      await Speech.stop();
    } catch (error) {
      console.log('Erreur arret voix :', error);
    }

    if (routeTravelMode === ROUTE_TRAVEL_MODE_WALKING) {
      Vibration.vibrate(
        step.maneuverType === 'arrive' ? [0, 140, 80, 180] : 80
      );
    }

    Speech.speak(`${prefix}${step.voiceText}`, {
      language: 'fr-FR',
      pitch: 1,
      rate: 0.96,
    });
  }, [routeTravelMode]);

  const clearSessionTimeout = React.useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  }, []);

  const emitJoinPresence = React.useCallback((pendingJoin) => {
    const activeSocket = socketRef.current;
    const activeLocation = pendingJoin?.location;

    if (
      !activeSocket?.connected ||
      !pendingJoin?.pseudo ||
      !pendingJoin?.code ||
      !activeLocation
    ) {
      return false;
    }

    activeSocket.emit('join_code', {
      pseudo: pendingJoin.pseudo,
      code: pendingJoin.code,
      invitedBy: pendingJoin.invitedBy || '',
    });

    activeSocket.emit('send_location', {
      code: pendingJoin.code,
      latitude: activeLocation.latitude,
      longitude: activeLocation.longitude,
    });

    if (pendingJoin.destination) {
      activeSocket.emit('set_room_destination', {
        code: pendingJoin.code,
        destination: pendingJoin.destination,
      });
    }

    return true;
  }, []);

  const flushPendingJoin = React.useCallback(() => {
    if (!pendingJoinRef.current) {
      if (!socketRef.current?.connected) {
        socketRef.current?.connect?.();
      }

      return false;
    }

    if (!socketRef.current?.connected) {
      socketRef.current?.connect?.();
      return false;
    }

    return emitJoinPresence(pendingJoinRef.current);
  }, [emitJoinPresence]);

  const attachSocketListeners = React.useCallback((activeSocket, connectMessage) => {
    activeSocket.on('connect', () => {
      setSocketId(activeSocket.id || '');
      setSocketOnline(true);
      setSocketError('');
      console.log(connectMessage);
      flushPendingJoin();
    });

    activeSocket.on('room_users', (users) => {
      setRoomUsers(users);
      pendingJoinRef.current = null;
      clearSessionTimeout();
      setSessionConnectionMessage('');
    });

    activeSocket.on('room_destination', (destination) => {
      if (destination && hasCoordinates(destination)) {
        setRoomDestination(destination);
        setSessionMode(SESSION_MODE_MEETING);
        return;
      }

      setRoomDestination(null);
    });

    activeSocket.on('connect_error', () => {
      setSocketOnline(false);
      setSocketError("Impossible de contacter le serveur");

      if (pendingJoinRef.current) {
        setSessionConnectionMessage('Reconnexion a la session...');
      }
    });

    activeSocket.on('disconnect', (reason) => {
      setSocketOnline(false);
      setSocketError(reason === 'io client disconnect' ? '' : 'Serveur indisponible');
    });

    activeSocket.on('room_activity_history', (activity) => {
      setActivityHistory(Array.isArray(activity) ? activity : []);
    });

    activeSocket.on('room_activity_event', (activityItem) => {
      if (!activityItem?.id) {
        return;
      }

      setActivityHistory((currentActivity) =>
        upsertActivityItem(currentActivity, activityItem)
      );

      if (activityItem.pseudo !== pseudoRef.current) {
        setIncomingSignal(activityItem);
      }
    });
  }, [clearSessionTimeout, flushPendingJoin]);

  useEffect(() => {
    if (!sessionConnectionMessage || !pendingJoinRef.current) {
      return;
    }

    const retryInterval = setInterval(() => {
      flushPendingJoin();
    }, 1200);

    return () => {
      clearInterval(retryInterval);
    };
  }, [flushPendingJoin, sessionConnectionMessage]);

  useEffect(() => {
    clearSessionTimeout();

    if (!sessionConnectionMessage) {
      return;
    }

    sessionTimeoutRef.current = setTimeout(() => {
      if (!pendingJoinRef.current) {
        return;
      }

      pendingJoinRef.current = null;

      if (watchRef.current) {
        watchRef.current.remove();
        watchRef.current = null;
      }

      socketRef.current?.disconnect();
      setJoined(false);
      locationRef.current = null;
      setLocation(null);
      setRoomUsers([]);
      setRoomDestination(null);
      setSocketId('');
      setRouteCoordinates([]);
      setRouteTarget(null);
      setRouteDistance(null);
      setRouteDuration(null);
      setRouteSteps([]);
      setRouteIsFallback(false);
      setRouteStartDistance(null);
      setRouteStartTargetId('');
      routeRequestRef.current = { targetKey: '', origin: null, requestedAt: 0 };
      routeDurationPerMeterRef.current = null;
      cameraHeadingRef.current = null;
      sessionStartVoiceKeyRef.current = '';
      setActivityHistory([]);
      setIncomingSignal(null);
      setSessionConnectionMessage('');
      setInviteHostPseudo('');
      setMapFollowEnabled(false);

      Alert.alert(
        'Serveur indisponible',
        'Impossible de joindre le serveur jarrive. Verifie que https://api.ping-app.org est en ligne et que Socket.IO est accessible.'
      );
    }, 12000);

    return () => {
      clearSessionTimeout();
    };
  }, [clearSessionTimeout, sessionConnectionMessage]);

  useEffect(() => {
    loadSavedData();

    socketRef.current = io(SERVER_URL);
    attachSocketListeners(socketRef.current, 'Connecte au serveur');

    return () => {
      if (watchRef.current) {
        watchRef.current.remove();
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (cameraAnimationTimeoutRef.current) {
        clearTimeout(cameraAnimationTimeoutRef.current);
        cameraAnimationTimeoutRef.current = null;
      }
      if (pendingMeetingAddressSearchTimerRef.current) {
        clearTimeout(pendingMeetingAddressSearchTimerRef.current);
        pendingMeetingAddressSearchTimerRef.current = null;
      }
    };
  }, [attachSocketListeners]);

  useEffect(() => {
    if (!incomingSignal) {
      return;
    }

    const timeout = setTimeout(() => {
      setIncomingSignal(null);
    }, 3200);

    return () => {
      clearTimeout(timeout);
    };
  }, [incomingSignal]);

  useEffect(() => {
    const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
      setKeyboardVisible(true);
    });
    const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardVisible(false);

      if (pendingMeetingAddressSearchRef.current) {
        startSubmittedMeetingAddressSearch(pendingMeetingAddressSearchRef.current);
      }
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, [flushPendingJoin, startSubmittedMeetingAddressSearch]);

  const ensureMeetingSearchOrigin = React.useCallback(async () => {
    if (location) {
      setMeetingSearchOrigin(location);
      return location;
    }

    if (meetingSearchOrigin) {
      return meetingSearchOrigin;
    }

    if (meetingOriginRequestRef.current) {
      return null;
    }

    try {
      meetingOriginRequestRef.current = true;
      let permission = await Location.getForegroundPermissionsAsync();

      if (permission.status !== 'granted' && permission.canAskAgain) {
        permission = await Location.requestForegroundPermissionsAsync();
      }

      if (permission.status !== 'granted') {
        return null;
      }

      const currentPosition = await getPreciseCurrentPositionAsync();
      const currentCoords = normalizeLocationCoords(currentPosition);

      if (!currentCoords) {
        return null;
      }

      setMeetingSearchOrigin(currentCoords);
      return currentCoords;
    } catch (error) {
      console.log('Erreur position recherche adresse :', error);
      return null;
    } finally {
      meetingOriginRequestRef.current = false;
    }
  }, [location, meetingSearchOrigin]);

  useEffect(() => {
    if (!addressSearchModalVisible) {
      return undefined;
    }

    setMeetingAddressSearchActive(true);
    ensureMeetingSearchOrigin();
    addressResultsKeyboardDismissKeyRef.current = '';

    const focusTimer = setTimeout(() => {
      meetingAddressInputRef.current?.focus();
    }, 120);

    return () => {
      clearTimeout(focusTimer);
    };
  }, [
    addressSearchModalVisible,
    ensureMeetingSearchOrigin,
  ]);

  useEffect(() => {
    const trimmedQuery = meetingAddressSubmittedQuery.trim();

    if (
      !addressSearchModalVisible ||
      meetingAddressLoading ||
      trimmedQuery.length < 3 ||
      meetingAddressLastSearchedQuery !== trimmedQuery
    ) {
      return undefined;
    }

    const dismissKey = `${trimmedQuery}:${meetingAddressSuggestions.length}:${meetingAddressError}`;

    if (addressResultsKeyboardDismissKeyRef.current === dismissKey) {
      return undefined;
    }

    addressResultsKeyboardDismissKeyRef.current = dismissKey;

    const dismissTimer = setTimeout(() => {
      meetingAddressInputRef.current?.blur();
      Keyboard.dismiss();
    }, 80);

    return () => {
      clearTimeout(dismissTimer);
    };
  }, [
    addressSearchModalVisible,
    meetingAddressError,
    meetingAddressLastSearchedQuery,
    meetingAddressLoading,
    meetingAddressSubmittedQuery,
    meetingAddressSuggestions.length,
  ]);

  useEffect(() => {
    latestLocationRef.current = location;

    if (location) {
      const shouldRefreshSearchOrigin =
        !meetingSearchOrigin ||
        getDistanceBetweenCoordinates(location, meetingSearchOrigin) > 150;

      if (shouldRefreshSearchOrigin) {
        setMeetingSearchOrigin(location);
      }
    }
  }, [location, meetingSearchOrigin]);

  useEffect(() => {
    const trimmedQuery = meetingAddressSubmittedQuery.trim();

    if (sessionMode !== SESSION_MODE_MEETING || trimmedQuery.length < 3) {
      setMeetingAddressLoading(false);
      setMeetingAddressLastSearchedQuery('');
      return;
    }

    setMeetingAddressLoading(true);
    setMeetingAddressError('');
    let cancelled = false;
    const loadingGuardTimer = setTimeout(() => {
      if (!cancelled) {
        setMeetingAddressLastSearchedQuery(trimmedQuery);
        setMeetingAddressLoading(false);
      }
    }, ADDRESS_SEARCH_TIMEOUT_MS + 1200);
    const searchTimer = setTimeout(async () => {
      try {
        const origin = meetingSearchOrigin || latestLocationRef.current || null;

        const queryHasHouseNumber = /\d/.test(trimmedQuery);
        const queryParts = [
          'format=jsonv2',
          'addressdetails=1',
          'extratags=1',
          'namedetails=1',
          'dedupe=1',
          'limit=10',
          `q=${encodeURIComponent(trimmedQuery)}`,
        ];

        if (origin) {
          const latitudeDelta = 0.08;
          const longitudeDelta = 0.08;
          const left = origin.longitude - longitudeDelta;
          const right = origin.longitude + longitudeDelta;
          const top = origin.latitude + latitudeDelta;
          const bottom = origin.latitude - latitudeDelta;

          queryParts.push(
            `viewbox=${left},${top},${right},${bottom}`,
            'bounded=0'
          );
        }

        const [
          nearbyAddressResult,
          nearbyHouseNumberResult,
          nationalAddressResult,
          nationalHouseNumberResult,
          openStreetMapResult,
        ] = await Promise.allSettled([
          fetchFrenchAddressPayload(trimmedQuery, origin, { limit: 10 }),
          queryHasHouseNumber
            ? fetchFrenchAddressPayload(trimmedQuery, origin, {
                limit: 10,
                type: 'housenumber',
              })
            : Promise.resolve({ features: [] }),
          fetchFrenchAddressPayload(trimmedQuery, null, { limit: 10 }),
          queryHasHouseNumber
            ? fetchFrenchAddressPayload(trimmedQuery, null, {
                limit: 10,
                type: 'housenumber',
              })
            : Promise.resolve({ features: [] }),
          origin
            ? fetchJsonWithTimeout(
                `https://nominatim.openstreetmap.org/search?${queryParts.join('&')}`,
                {
                  timeoutMs: BUSINESS_SEARCH_TIMEOUT_MS,
                  errorLabel: 'Geocoding HTTP',
                  headers: {
                    Accept: 'application/json',
                    'Accept-Language': 'fr',
                  },
                }
              )
            : Promise.resolve([]),
        ]);

        const openStreetMapPayload =
          openStreetMapResult.status === 'fulfilled' ? openStreetMapResult.value : [];
        const openStreetMapSuggestions = (
          Array.isArray(openStreetMapPayload) ? openStreetMapPayload : []
        )
          .map((suggestion) => normalizeAddressSuggestion(suggestion, origin))
          .filter(Boolean);
        const suggestions = mergeAddressSuggestions([
          normalizeFrenchAddressPayload(
            nearbyHouseNumberResult.status === 'fulfilled'
              ? nearbyHouseNumberResult.value
              : null,
            origin,
            -1
          ),
          normalizeFrenchAddressPayload(
            nearbyAddressResult.status === 'fulfilled'
              ? nearbyAddressResult.value
              : null,
            origin
          ),
          normalizeFrenchAddressPayload(
            nationalHouseNumberResult.status === 'fulfilled'
              ? nationalHouseNumberResult.value
              : null,
            origin,
            0
          ),
          normalizeFrenchAddressPayload(
            nationalAddressResult.status === 'fulfilled'
              ? nationalAddressResult.value
              : null,
            origin,
            1
          ),
          openStreetMapSuggestions,
        ]);

        if (
          !suggestions.length &&
          nearbyAddressResult.status === 'rejected' &&
          nearbyHouseNumberResult.status === 'rejected' &&
          nationalAddressResult.status === 'rejected' &&
          nationalHouseNumberResult.status === 'rejected' &&
          openStreetMapResult.status === 'rejected'
        ) {
          throw nationalAddressResult.reason || openStreetMapResult.reason;
        }

        if (!cancelled) {
          setMeetingAddressLastSearchedQuery(trimmedQuery);
          setMeetingAddressSuggestions(suggestions);
          setMeetingAddressError('');
        }
      } catch (error) {
        console.log('Erreur recherche adresse :', error);

        if (!cancelled) {
          setMeetingAddressSuggestions([]);
          setMeetingAddressError('Recherche indisponible');
        }
      } finally {
        if (!cancelled) {
          clearTimeout(loadingGuardTimer);
          setMeetingAddressLoading(false);
        }
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(searchTimer);
      clearTimeout(loadingGuardTimer);
    };
  }, [
    meetingAddressSearchNonce,
    meetingAddressSubmittedQuery,
    meetingSearchOrigin,
    sessionMode,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadRoute = async () => {
      if (!location) {
        setRouteCoordinates([]);
        setRouteTarget(null);
        setRouteDistance(null);
        setRouteDuration(null);
        setRouteSteps([]);
        setRouteIsFallback(false);
        return;
      }

      const closestUser = getClosestUser(roomUsers, location, socketId, pseudo);
      const nextRouteTarget =
        sessionMode === SESSION_MODE_MEETING ? roomDestination : closestUser;

      if (!nextRouteTarget || !hasCoordinates(nextRouteTarget)) {
        setRouteCoordinates([]);
        setRouteTarget(null);
        setRouteDistance(null);
        setRouteDuration(null);
        setRouteSteps([]);
        setRouteIsFallback(false);
        return;
      }

      try {
        const targetKey = [
          sessionMode,
          routeTravelMode,
          nextRouteTarget.id || getRouteTargetLabel(nextRouteTarget),
          Number(nextRouteTarget.latitude).toFixed(5),
          Number(nextRouteTarget.longitude).toFixed(5),
        ].join(':');
        const lastRouteRequest = routeRequestRef.current;
        const distanceFromLastRouteOrigin = lastRouteRequest.origin
          ? getDistanceBetweenCoordinates(location, lastRouteRequest.origin)
          : Number.POSITIVE_INFINITY;
        const routeRequestAge = Date.now() - lastRouteRequest.requestedAt;
        const recalculationDistanceMeters =
          routeTravelMode === ROUTE_TRAVEL_MODE_WALKING
            ? PEDESTRIAN_ROUTE_RECALCULATION_DISTANCE_METERS
            : ROUTE_RECALCULATION_DISTANCE_METERS;
        const recalculationIntervalMs =
          routeTravelMode === ROUTE_TRAVEL_MODE_WALKING
            ? PEDESTRIAN_ROUTE_RECALCULATION_INTERVAL_MS
            : ROUTE_RECALCULATION_INTERVAL_MS;
        const canReuseRoute =
          lastRouteRequest.targetKey === targetKey &&
          routeCoordinates.length > 1 &&
          !routeIsFallback &&
          typeof distanceFromLastRouteOrigin === 'number' &&
          (distanceFromLastRouteOrigin < recalculationDistanceMeters ||
            routeRequestAge < recalculationIntervalMs);
        const shouldWaitBeforeFallbackRetry =
          lastRouteRequest.targetKey === targetKey &&
          routeIsFallback &&
          typeof distanceFromLastRouteOrigin === 'number' &&
          distanceFromLastRouteOrigin < recalculationDistanceMeters &&
          routeRequestAge < recalculationIntervalMs;

        if (canReuseRoute) {
          const anchoredRouteCoordinates = [
            {
              latitude: location.latitude,
              longitude: location.longitude,
            },
            ...routeCoordinates.slice(1),
          ];
          const remainingDistance = getRemainingDistanceAlongRoute(
            location,
            anchoredRouteCoordinates
          );
          const routeStartGap = getDistanceBetweenCoordinates(
            location,
            routeCoordinates[0]
          );

          if (typeof remainingDistance === 'number') {
            setRouteDistance(remainingDistance);

            if (typeof routeDurationPerMeterRef.current === 'number') {
              setRouteDuration(remainingDistance * routeDurationPerMeterRef.current);
            }
          }

          if (typeof routeStartGap === 'number' && routeStartGap > 2) {
            setRouteCoordinates(anchoredRouteCoordinates);
          }

          return;
        }

        if (shouldWaitBeforeFallbackRetry) {
          setRouteDistance(getDistanceBetweenCoordinates(location, nextRouteTarget));
          return;
        }

        routeRequestRef.current = {
          targetKey,
          origin: {
            latitude: location.latitude,
            longitude: location.longitude,
          },
          requestedAt: Date.now(),
        };

        const routeProfiles = [routeTravelMode];
        let routingResult = null;
        let routingError = null;

        for (const profile of routeProfiles) {
          const routingServices = ROUTING_SERVICES[profile] || [];

          for (const routingService of routingServices) {
            try {
              routingResult = await fetchRouteForProfile(
                profile,
                routingService,
                location,
                nextRouteTarget
              );
              break;
            } catch (error) {
              routingError = error;
            }
          }

          if (routingResult) {
            break;
          }
        }

        if (!routingResult) {
          throw routingError || new Error('Aucun trajet trouve');
        }

        if (!cancelled) {
          const { coordinates, route } = routingResult;
          const nextRouteSteps = buildRouteSteps(
            route,
            nextRouteTarget,
            routeTravelMode
          );
          const nextRouteDistance = route?.distance ?? null;
          const nextRouteDuration = route?.duration ?? null;
          const nextRouteCoordinates = [
            {
              latitude: location.latitude,
              longitude: location.longitude,
            },
            ...coordinates,
          ];

          setRouteTarget(nextRouteTarget);
          setRouteCoordinates(nextRouteCoordinates);
          setRouteDistance(nextRouteDistance);
          setRouteDuration(nextRouteDuration);
          setRouteIsFallback(false);
          routeDurationPerMeterRef.current =
            typeof nextRouteDistance === 'number' &&
            nextRouteDistance > 0 &&
            typeof nextRouteDuration === 'number'
              ? nextRouteDuration / nextRouteDistance
              : null;
          setRouteSteps(nextRouteSteps);
        }
      } catch (error) {
        console.log('Erreur itineraire :', error);

        if (!cancelled) {
          const targetLabel = getRouteTargetLabel(nextRouteTarget);
          const directDistance = getDistanceBetweenCoordinates(location, nextRouteTarget);

          setRouteTarget(nextRouteTarget);
          setRouteCoordinates([
            {
              latitude: location.latitude,
              longitude: location.longitude,
            },
            {
              latitude: nextRouteTarget.latitude,
              longitude: nextRouteTarget.longitude,
            },
          ]);
          setRouteDistance(directDistance);
          setRouteDuration(null);
          setRouteIsFallback(true);
          routeDurationPerMeterRef.current = null;
          cameraHeadingRef.current = null;
          setRouteSteps([
            {
              id: `${nextRouteTarget.id}-fallback-arrive`,
              voiceKey: `${nextRouteTarget.id}-arrive`,
              instruction:
                routeTravelMode === ROUTE_TRAVEL_MODE_WALKING
                  ? targetLabel
                    ? `Marche jusqu a ${targetLabel}`
                    : 'Marche jusqu a la destination'
                  : targetLabel
                    ? `Continuez jusqu a ${targetLabel}`
                    : 'Continuez jusqu a destination',
              voiceText:
                routeTravelMode === ROUTE_TRAVEL_MODE_WALKING
                  ? targetLabel
                    ? `Marche jusqu a ${targetLabel}`
                    : 'Marche jusqu a la destination'
                  : targetLabel
                    ? `Continuez jusqu a ${targetLabel}`
                    : 'Continuez jusqu a destination',
              distance: 0,
              duration: 0,
              distanceLabel: null,
              durationLabel: null,
              roadLabel: '',
              location: {
                latitude: nextRouteTarget.latitude,
                longitude: nextRouteTarget.longitude,
              },
              maneuverType: 'arrive',
              travelMode: routeTravelMode,
            },
          ]);
        }
      }
    };

    loadRoute();

    return () => {
      cancelled = true;
    };
  }, [
    location,
    pseudo,
    roomDestination,
    roomUsers,
    routeCoordinates,
    routeIsFallback,
    routeTravelMode,
    sessionMode,
    socketId,
  ]);

  useEffect(() => {
    if (!routeTarget?.id || typeof routeDistance !== 'number') {
      if (!routeTarget) {
        setRouteStartDistance(null);
        setRouteStartTargetId('');
      }
      return;
    }

    if (routeStartTargetId !== routeTarget.id) {
      setRouteStartTargetId(routeTarget.id);
      setRouteStartDistance(routeDistance);
      return;
    }

    setRouteStartDistance((currentStartDistance) => {
      if (typeof currentStartDistance !== 'number') {
        return routeDistance;
      }

      return routeDistance > currentStartDistance
        ? routeDistance
        : currentStartDistance;
    });
  }, [routeDistance, routeTarget, routeStartTargetId]);

  useEffect(() => {
    const targetId = routeTarget?.id || '';

    if (!targetId) {
      routeVoiceTargetIdRef.current = '';
      sessionStartVoiceKeyRef.current = '';
      resetSpokenSteps();
      return;
    }

    if (routeVoiceTargetIdRef.current !== targetId) {
      const startVoiceKey = [
        sessionMode,
        targetId,
        pseudo.trim(),
        String(inviteHostPseudo || routeTarget?.invitedBy || '').trim(),
      ].join(':');
      const startVoiceText = getSessionStartVoiceText({
        routeTarget,
        currentPseudo: pseudo,
        inviteHostPseudo,
        sessionMode,
      });

      routeVoiceTargetIdRef.current = targetId;
      resetSpokenSteps();

      if (startVoiceText) {
        speakSessionStart(startVoiceText, startVoiceKey);
      } else {
        Speech.stop();
      }
    }
  }, [
    inviteHostPseudo,
    pseudo,
    resetSpokenSteps,
    routeTarget,
    routeTarget?.id,
    sessionMode,
    speakSessionStart,
  ]);

  useEffect(() => {
    if (!location || !routeSteps.length) {
      return;
    }

    const nextStepToSpeak = routeSteps.find(
      (step) => !spokenStepKeysRef.current.has(step.voiceKey)
    );

    if (!nextStepToSpeak) {
      return;
    }

    const distanceToStep = getDistanceBetweenCoordinates(location, nextStepToSpeak.location);

    if (typeof distanceToStep !== 'number') {
      return;
    }

    if (distanceToStep > getVoiceTriggerDistance(nextStepToSpeak, routeTravelMode)) {
      return;
    }

    spokenStepKeysRef.current.add(nextStepToSpeak.voiceKey);
    setSpokenStepKeys(Array.from(spokenStepKeysRef.current));

    speakRouteStep(nextStepToSpeak);
  }, [location, routeSteps, routeTravelMode, speakRouteStep]);

  useEffect(() => {
    if (
      routeTravelMode !== ROUTE_TRAVEL_MODE_WALKING ||
      !routeTarget?.id ||
      typeof routeDistance !== 'number'
    ) {
      return;
    }

    const vibrationKey = `${routeTarget.id}:near`;

    if (
      routeDistance <= PEDESTRIAN_NEAR_DESTINATION_DISTANCE_METERS &&
      walkingNearDestinationVibrationRef.current !== vibrationKey
    ) {
      walkingNearDestinationVibrationRef.current = vibrationKey;
      Vibration.vibrate([0, 90, 80, 90]);
      return;
    }

    if (
      routeDistance > PEDESTRIAN_NEAR_DESTINATION_DISTANCE_METERS + 40 &&
      walkingNearDestinationVibrationRef.current === vibrationKey
    ) {
      walkingNearDestinationVibrationRef.current = '';
    }
  }, [routeDistance, routeTarget?.id, routeTravelMode]);

  const animateMapToUser = React.useCallback((duration = 650) => {
    if (!IS_NATIVE_MAPS || !mapRef.current || !location) {
      return;
    }

    const fallbackTarget =
      routeTarget ||
      (sessionMode === SESSION_MODE_MEETING
        ? roomDestination
        : getClosestUser(roomUsers, location, socketId, pseudo));
    const headingTarget = getRouteHeadingTarget(
      location,
      routeCoordinates,
      fallbackTarget
    );
    const rawHeading = headingTarget
      ? getBearingBetweenCoordinates(location, headingTarget)
      : 0;
    const cameraSettings = getCameraSettingsForTravelMode(
      routeTravelMode,
      !!headingTarget
    );
    const heading = smoothHeading(
      cameraHeadingRef.current,
      rawHeading,
      cameraSettings.smoothing
    );

    cameraHeadingRef.current = heading;

    const cameraCenter =
      headingTarget && cameraSettings.centerAheadMeters
        ? getCoordinateAtBearingDistance(
            location,
            heading,
            cameraSettings.centerAheadMeters
          )
        : location;

    isCameraAnimatingRef.current = true;

    if (cameraAnimationTimeoutRef.current) {
      clearTimeout(cameraAnimationTimeoutRef.current);
    }

    mapRef.current.animateCamera(
      {
        center: {
          latitude: cameraCenter.latitude,
          longitude: cameraCenter.longitude,
        },
        heading,
        pitch: cameraSettings.pitch,
        zoom: cameraSettings.zoom,
      },
      { duration }
    );

    cameraAnimationTimeoutRef.current = setTimeout(() => {
      isCameraAnimatingRef.current = false;
      cameraAnimationTimeoutRef.current = null;
    }, duration + 350);
  }, [
    location,
    pseudo,
    roomDestination,
    roomUsers,
    routeCoordinates,
    routeTarget,
    routeTravelMode,
    sessionMode,
    socketId,
  ]);

  useEffect(() => {
    if (!mapFollowEnabled) {
      return;
    }

    animateMapToUser(routeTravelMode === ROUTE_TRAVEL_MODE_WALKING ? 1050 : 850);
  }, [animateMapToUser, mapFollowEnabled, routeTravelMode]);

  useEffect(() => {
    if (!joined) {
      deactivateKeepAwake(KEEP_AWAKE_TAG).catch((error) => {
        console.log('Erreur desactivation ecran actif :', error);
      });
      return;
    }

    activateKeepAwakeAsync(KEEP_AWAKE_TAG).catch((error) => {
      console.log('Erreur maintien ecran actif :', error);
    });

    return () => {
      deactivateKeepAwake(KEEP_AWAKE_TAG).catch((error) => {
        console.log('Erreur desactivation ecran actif :', error);
      });
    };
  }, [joined]);

  const recenterMapOnUser = React.useCallback(() => {
    setMapFollowEnabled(true);
    animateMapToUser(routeTravelMode === ROUTE_TRAVEL_MODE_WALKING ? 750 : 550);
  }, [animateMapToUser, routeTravelMode]);

  const enableAutomaticMapMode = React.useCallback(() => {
    setMapFollowEnabled(true);
    animateMapToUser(routeTravelMode === ROUTE_TRAVEL_MODE_WALKING ? 750 : 550);
  }, [animateMapToUser, routeTravelMode]);

  const pauseMapFollow = React.useCallback(() => {
    isCameraAnimatingRef.current = false;

    if (cameraAnimationTimeoutRef.current) {
      clearTimeout(cameraAnimationTimeoutRef.current);
      cameraAnimationTimeoutRef.current = null;
    }

    setMapFollowEnabled(false);
  }, []);

  const enableManualMapMode = React.useCallback(() => {
    pauseMapFollow();
  }, [pauseMapFollow]);

  const updateRouteTravelMode = React.useCallback((nextTravelMode) => {
    setRouteTravelMode(nextTravelMode);
    setRouteCoordinates([]);
    setRouteDistance(null);
    setRouteDuration(null);
    setRouteSteps([]);
    setRouteIsFallback(false);
    setRouteStartDistance(null);
    setRouteStartTargetId('');
    setDeviceHeading(null);
    routeRequestRef.current = { targetKey: '', origin: null, requestedAt: 0 };
    routeDurationPerMeterRef.current = null;
    cameraHeadingRef.current = null;
    walkingNearDestinationVibrationRef.current = '';
    resetSpokenSteps();
  }, [resetSpokenSteps]);

  const loadSavedData = async () => {
    try {
      const savedPseudo = await AsyncStorage.getItem('flash_pseudo');
      const savedPhone = await AsyncStorage.getItem('flash_phone');
      const cleanSavedPseudo = isGeneratedInvitePseudo(savedPseudo) ? '' : savedPseudo;

      if (cleanSavedPseudo) {
        setPseudo((currentPseudo) => currentPseudo || cleanSavedPseudo);
      } else if (savedPseudo && isGeneratedInvitePseudo(savedPseudo)) {
        await AsyncStorage.removeItem('flash_pseudo');
      }

      if (savedPhone) {
        setPhone((currentPhone) => currentPhone || savedPhone);
      }
    } catch (error) {
      console.log('Erreur chargement stockage :', error);
    }
  };

  const saveData = React.useCallback(async (newPseudo, newCode, newPhone) => {
    try {
      await AsyncStorage.setItem('flash_pseudo', newPseudo);
      await AsyncStorage.setItem('flash_code', newCode);
      await AsyncStorage.setItem('flash_phone', newPhone);
    } catch (error) {
      console.log('Erreur sauvegarde stockage :', error);
    }
  }, []);

  const generateNewCode = React.useCallback(async () => {
    const nextCode = generateSessionCode();

    setCode(nextCode);
    setInviteHostPseudo('');

    try {
      await AsyncStorage.setItem('flash_code', nextCode);
    } catch (error) {
      console.log('Erreur sauvegarde code genere :', error);
    }
  }, []);

  const buildInviteMessage = (guestPseudo = '') => {
    const inviteSender = isGeneratedInvitePseudo(pseudo) ? '' : pseudo.trim();
    const inviteSenderLabel = inviteSender || 'Quelquun';
    const trimmedGuestPseudo = String(guestPseudo || '').trim();
    const inviteLink = buildInviteLink(
      code,
      inviteSender,
      sessionMode,
      trimmedGuestPseudo
    );
    const inviteTargetText = trimmedGuestPseudo ? ` ${trimmedGuestPseudo},` : '';

    if (sessionMode === SESSION_MODE_MEETING) {
      return `${inviteSenderLabel} te donne rendez-vous sur jarrive.${inviteTargetText} ouvre ce lien pour rejoindre le point de rendez-vous : ${inviteLink}`;
    }

    return `${inviteSenderLabel} t'invite sur jarrive.${inviteTargetText} ouvre ce lien pour rejoindre la session : ${inviteLink}`;
  };

  const pasteCodeFromClipboard = async () => {
    try {
      const clipboardText = await Clipboard.getStringAsync();
      const trimmedCode = clipboardText.trim();

      if (!trimmedCode) {
        Alert.alert('Presse-papiers vide', 'Aucun code a coller.');
        return;
      }

      setCode(trimmedCode);
      setInviteHostPseudo('');
    } catch (error) {
      console.log('Erreur collage presse-papiers :', error);
      Alert.alert('Erreur', 'Impossible de coller le code.');
    }
  };

  const pastePhoneFromClipboard = async () => {
    try {
      const clipboardText = await Clipboard.getStringAsync();
      const trimmedPhone = clipboardText.trim();

      if (!trimmedPhone) {
        Alert.alert('Presse-papiers vide', 'Aucun numero a coller.');
        return;
      }

      setPhone(trimmedPhone);
    } catch (error) {
      console.log('Erreur collage telephone :', error);
      Alert.alert('Erreur', 'Impossible de coller le numero.');
    }
  };

  const setMeetingDestination = React.useCallback((coordinate, label, description) => {
    const nextDestination = buildMeetingDestination(coordinate, label, description);
    const trimmedCode = code.trim();

    if (!nextDestination) {
      return;
    }

    setSessionMode(SESSION_MODE_MEETING);
    setRoomDestination(nextDestination);
    resetSpokenSteps();
    setRouteStartDistance(null);
    setRouteStartTargetId('');
    routeRequestRef.current = { targetKey: '', origin: null, requestedAt: 0 };
    routeDurationPerMeterRef.current = null;
    cameraHeadingRef.current = null;
    setMapFollowEnabled(true);

    if (trimmedCode && joined) {
      socketRef.current?.emit('set_room_destination', {
        code: trimmedCode,
        destination: nextDestination,
      });
    }
  }, [code, joined, resetSpokenSteps]);

  const selectMeetingAddressSuggestion = React.useCallback((suggestion) => {
    const label = suggestion?.label || 'Point de rendez-vous';
    const description = suggestion?.description || '';

    Keyboard.dismiss();
    selectedMeetingAddressQueryRef.current = label;
    pendingMeetingAddressSearchRef.current = '';
    if (pendingMeetingAddressSearchTimerRef.current) {
      clearTimeout(pendingMeetingAddressSearchTimerRef.current);
      pendingMeetingAddressSearchTimerRef.current = null;
    }
    setMeetingAddressQuery(label);
    setMeetingAddressSubmittedQuery('');
    setMeetingAddressSuggestions([]);
    setMeetingAddressLoading(false);
    setMeetingAddressError('');
    setMeetingAddressSearchActive(false);
    setAddressSearchModalVisible(false);
    setMeetingDestination(suggestion, label, description);
  }, [setMeetingDestination]);

  const handleMapLongPress = React.useCallback((event) => {
    if (sessionMode !== SESSION_MODE_MEETING) {
      return;
    }

    Keyboard.dismiss();
    setMeetingAddressSearchActive(false);
    setMeetingAddressSuggestions([]);
    setMeetingAddressError('');
    setMeetingDestination(event?.nativeEvent?.coordinate);
  }, [sessionMode, setMeetingDestination]);

  const startLocationSharing = React.useCallback(async (activeCode) => {
    if (watchRef.current) {
      watchRef.current.remove();
      watchRef.current = null;
    }

    watchRef.current = await Location.watchPositionAsync(
      PRECISE_LOCATION_OPTIONS,
      (newLocation) => {
        const coords = normalizeLocationCoords(newLocation);

        if (!shouldUseLocationUpdate(coords, locationRef.current)) {
          return;
        }

        locationRef.current = coords;
        setLocation(coords);

        if (pendingJoinRef.current?.code === activeCode) {
          pendingJoinRef.current = {
            ...pendingJoinRef.current,
            location: coords,
          };
          flushPendingJoin();
        }

        if (socketRef.current) {
          socketRef.current.emit('send_location', {
            code: activeCode,
            latitude: coords.latitude,
            longitude: coords.longitude,
          });
        }
      }
    );
  }, [flushPendingJoin]);

  const joinSession = React.useCallback(async ({
    nextPseudo,
    nextCode,
    nextPhone = '',
    nextMode = sessionMode,
    nextInvitedBy = '',
    fromInvite = false,
  }) => {
    const trimmedPseudo = String(nextPseudo || '').trim();
    const trimmedCode = String(nextCode || '').trim();
    const trimmedPhone = String(nextPhone || '').trim();
    const trimmedInvitedBy = String(nextInvitedBy || '').trim();
    const normalizedNextMode = normalizeSessionMode(nextMode);

    if (!trimmedPseudo) {
      clearSessionTimeout();
      setSessionConnectionMessage('');
      Alert.alert('Pseudo manquant', 'Entre ton pseudo.');
      return false;
    }

    if (!trimmedCode) {
      clearSessionTimeout();
      setSessionConnectionMessage('');
      Alert.alert('Code manquant', 'Entre un code de session.');
      return false;
    }

    const activeJoinKey = `${trimmedPseudo}:${trimmedCode}:${normalizedNextMode}:${trimmedInvitedBy}`;
    const initialMeetingDestination =
      normalizedNextMode === SESSION_MODE_MEETING && roomDestination
        ? roomDestination
        : null;

    if (activeJoinKeyRef.current === activeJoinKey) {
      return true;
    }

    try {
      activeJoinKeyRef.current = activeJoinKey;
      clearSessionTimeout();
      setSessionConnectionMessage('Connexion a la session...');
      await saveData(trimmedPseudo, trimmedCode, trimmedPhone);
      setPseudo(trimmedPseudo);
      setCode(trimmedCode);
      setPhone(trimmedPhone);
      setInviteHostPseudo(trimmedInvitedBy);
      setSessionMode(normalizedNextMode);

      const { status } = await Location.requestForegroundPermissionsAsync();

      if (status !== 'granted') {
        clearSessionTimeout();
        setSessionConnectionMessage('');
        Alert.alert(
          'Permission refusee',
          fromInvite
            ? "La localisation est necessaire pour rejoindre l'invitation."
            : 'La localisation est necessaire.'
        );
        return false;
      }

      setRoomUsers([]);
      setRoomDestination(initialMeetingDestination);
      setRouteCoordinates([]);
      setRouteTarget(null);
      setRouteDistance(null);
      setRouteDuration(null);
      setRouteSteps([]);
      setRouteIsFallback(false);
      setRouteStartDistance(null);
      setRouteStartTargetId('');
      routeRequestRef.current = { targetKey: '', origin: null, requestedAt: 0 };
      routeDurationPerMeterRef.current = null;
      cameraHeadingRef.current = null;
      resetSpokenSteps();
      setActivityHistory([]);
      setIncomingSignal(null);
      setDetailsExpanded(false);
      setMapFollowEnabled(!!initialMeetingDestination);

      const currentLocation = await getPreciseCurrentPositionAsync();
      const currentCoords = normalizeLocationCoords(currentLocation);

      if (!currentCoords) {
        clearSessionTimeout();
        setSessionConnectionMessage('');
        Alert.alert('Position indisponible', 'Impossible de recuperer une position GPS precise.');
        return false;
      }

      locationRef.current = currentCoords;
      setLocation(currentCoords);
      setJoined(true);
      pendingJoinRef.current = {
        pseudo: trimmedPseudo,
        code: trimmedCode,
        invitedBy: trimmedInvitedBy,
        location: currentCoords,
        destination: initialMeetingDestination,
      };
      flushPendingJoin();

      if (!socketRef.current?.connected) {
        socketRef.current?.connect?.();
      }

      await startLocationSharing(trimmedCode);
      return true;
    } catch (error) {
      clearSessionTimeout();
      setSessionConnectionMessage('');
      Alert.alert('Erreur', 'Impossible de rejoindre.');
      console.log(error);
      return false;
    } finally {
      activeJoinKeyRef.current = '';
    }
  }, [
    clearSessionTimeout,
    flushPendingJoin,
    resetSpokenSteps,
    saveData,
    roomDestination,
    sessionMode,
    startLocationSharing,
  ]);

  const getLocationAndJoin = async () => {
    await joinSession({
      nextPseudo: pseudo,
      nextCode: code,
      nextPhone: phone,
      nextMode: sessionMode,
      nextInvitedBy: inviteHostPseudo,
      fromInvite: false,
    });
  };

  const scheduleInviteJoin = React.useCallback(
    (invitePseudo, inviteCode, inviteMode, inviteHost = '') => {
      if (inviteJoinTimerRef.current) {
        clearTimeout(inviteJoinTimerRef.current);
        inviteJoinTimerRef.current = null;
      }

      inviteJoinTimerRef.current = setTimeout(
        () => {
          inviteJoinTimerRef.current = null;
          joinSession({
            nextPseudo: invitePseudo,
            nextCode: inviteCode,
            nextPhone: '',
            nextMode: inviteMode,
            nextInvitedBy: inviteHost,
            fromInvite: true,
          });
        },
        Platform.OS === 'android' ? 600 : 0
      );
    },
    [joinSession]
  );

  useEffect(() => {
    return () => {
      if (inviteJoinTimerRef.current) {
        clearTimeout(inviteJoinTimerRef.current);
        inviteJoinTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const inviteCodeFromRoute = String(inviteParams.code || '').trim();
    const inviteHostFromRoute = String(inviteParams.host || '').trim();
    const inviteGuestFromRoute = String(inviteParams.guest || '').trim();
    const inviteModeFromRoute = normalizeSessionMode(inviteParams.mode);
    const shouldAutoJoin =
      Platform.OS !== 'android' &&
      String(inviteParams.autoJoin || inviteParams.autojoin || '') === '1';

    if (!inviteCodeFromRoute) {
      return;
    }

    const inviteKey = buildHandledInviteKey(
      inviteCodeFromRoute,
      inviteHostFromRoute,
      inviteModeFromRoute,
      inviteGuestFromRoute
    );

    if (handledInviteUrlsRef.current.has(inviteKey)) {
      return;
    }

    handledInviteUrlsRef.current.add(inviteKey);

    const invitePseudo = getInvitePrefillPseudo(pseudo, inviteGuestFromRoute);

    setPseudo(invitePseudo);
    setCode(inviteCodeFromRoute);
    setPhone('');
    setInviteHostPseudo(inviteHostFromRoute);
    setSessionMode(inviteModeFromRoute);

    if (shouldAutoJoin) {
      scheduleInviteJoin(
        invitePseudo,
        inviteCodeFromRoute,
        inviteModeFromRoute,
        inviteHostFromRoute
      );
    }
  }, [
    inviteParams.autoJoin,
    inviteParams.autojoin,
    inviteParams.code,
    inviteParams.guest,
    inviteParams.host,
    inviteParams.mode,
    pseudo,
    scheduleInviteJoin,
  ]);

  useEffect(() => {
    let isActive = true;

    const handleInviteUrl = async (incomingUrl) => {
      if (!incomingUrl || handledInviteUrlsRef.current.has(incomingUrl)) {
        return;
      }

      const invitePayload = parseInviteLink(incomingUrl);

      if (!invitePayload) {
        return;
      }

      const inviteKey = buildHandledInviteKey(
        invitePayload.code,
        invitePayload.host,
        invitePayload.mode,
        invitePayload.guest
      );

      if (handledInviteUrlsRef.current.has(inviteKey)) {
        return;
      }

      handledInviteUrlsRef.current.add(incomingUrl);
      handledInviteUrlsRef.current.add(inviteKey);

      const invitePseudo = getInvitePrefillPseudo(
        pseudoRef.current,
        invitePayload.guest
      );

      if (!isActive) {
        return;
      }

      setPseudo(invitePseudo);
      setCode(invitePayload.code);
      setPhone('');
      setInviteHostPseudo(invitePayload.host);
      setSessionMode(invitePayload.mode);

      if (Platform.OS !== 'android' && invitePayload.autoJoin) {
        scheduleInviteJoin(
          invitePseudo,
          invitePayload.code,
          invitePayload.mode,
          invitePayload.host
        );
      }
    };

    const initializeInviteFlow = async () => {
      const initialUrl = await Linking.getInitialURL();
      await handleInviteUrl(initialUrl);
    };

    initializeInviteFlow();

    const urlSubscription = Linking.addEventListener('url', ({ url }) => {
      handleInviteUrl(url);
    });

    return () => {
      isActive = false;
      urlSubscription.remove();
    };
  }, [scheduleInviteJoin]);

  const shareCode = async () => {
    if (!pseudo.trim() || isGeneratedInvitePseudo(pseudo)) {
      Alert.alert('Pseudo manquant', 'Entre ton prenom avant de partager un lien.');
      return;
    }

    if (!code.trim()) {
      Alert.alert('Code manquant', 'Entre un code de session avant de le partager.');
      return;
    }

    setInviteGuestPseudo('');
    setInvitePromptError('');
    setInvitePromptVisible(true);
  };

  const closeInvitePrompt = () => {
    setInvitePromptVisible(false);
    setInvitePromptError('');
    setInviteGuestPseudo('');
  };

  const confirmInviteShare = async () => {
    const trimmedGuestPseudo = inviteGuestPseudo.trim();

    if (!trimmedGuestPseudo) {
      setInvitePromptError('Entre le prenom ou surnom du contact.');
      return;
    }

    try {
      setInvitePromptVisible(false);
      await saveData(pseudo, code, phone);

      const inviteMessage = buildInviteMessage(trimmedGuestPseudo);
      const trimmedPhone = phone.replace(/\s+/g, '');

      if (trimmedPhone) {
        const separator = Platform.OS === 'ios' ? '&' : '?';
        const encodedMessage = encodeURIComponent(inviteMessage);
        const smsUrls =
          Platform.OS === 'android'
            ? [
                `sms:${trimmedPhone}?body=${encodedMessage}`,
                `smsto:${trimmedPhone}?body=${encodedMessage}`,
              ]
            : [`sms:${trimmedPhone}${separator}body=${encodedMessage}`];

        for (const smsUrl of smsUrls) {
          try {
            await Linking.openURL(smsUrl);
            return;
          } catch (smsError) {
            console.log('Erreur ouverture SMS :', smsError);
          }
        }

        await Share.share({
          message: inviteMessage,
        });
        return;
      }

      await Share.share({
        message: inviteMessage,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const openAppleMaps = async () => {
    if (!routeTarget || !hasCoordinates(routeTarget)) {
      Alert.alert('Destination indisponible', "Aucune destination n'est disponible pour cette session.");
      return;
    }

    try {
      const appleDirectionMode =
        routeTravelMode === ROUTE_TRAVEL_MODE_WALKING ? 'w' : 'd';
      const appleMapsUrl = `http://maps.apple.com/?daddr=${routeTarget.latitude},${routeTarget.longitude}&dirflg=${appleDirectionMode}`;
      await Linking.openURL(appleMapsUrl);
    } catch (error) {
      console.log('Erreur ouverture Apple Maps :', error);
      Alert.alert('Erreur', "Impossible d'ouvrir Apple Maps.");
    }
  };

  const openGoogleMaps = async () => {
    if (!routeTarget || !hasCoordinates(routeTarget)) {
      Alert.alert('Destination indisponible', "Aucune destination n'est disponible pour cette session.");
      return;
    }

    try {
      const googleTravelMode =
        routeTravelMode === ROUTE_TRAVEL_MODE_WALKING ? 'walking' : 'driving';
      const googleMapsAppUrl = `comgooglemaps://?daddr=${routeTarget.latitude},${routeTarget.longitude}&directionsmode=${googleTravelMode}`;
      const googleMapsWebUrl = `https://www.google.com/maps/dir/?api=1&destination=${routeTarget.latitude},${routeTarget.longitude}&travelmode=${googleTravelMode}`;

      if (Platform.OS === 'ios') {
        const canOpenGoogleMapsApp = await Linking.canOpenURL(googleMapsAppUrl);

        if (canOpenGoogleMapsApp) {
          await Linking.openURL(googleMapsAppUrl);
          return;
        }
      }

      await Linking.openURL(googleMapsWebUrl);
    } catch (error) {
      console.log('Erreur ouverture Google Maps :', error);
      Alert.alert('Erreur', "Impossible d'ouvrir Google Maps.");
    }
  };

  const sendQuickSignal = (label) => {
    if (!socketRef.current || !code.trim()) {
      Alert.alert('Signal indisponible', 'La session nest pas encore connectee.');
      return;
    }

    socketRef.current.emit('send_room_signal', {
      code: code.trim(),
      signal: {
        label,
        kind: 'manual',
        mode: 'system',
      },
    });

    Vibration.vibrate(60);
  };

  const stopSharing = () => {
    if (watchRef.current) {
      watchRef.current.remove();
      watchRef.current = null;
    }
    if (headingWatchRef.current) {
      headingWatchRef.current.remove();
      headingWatchRef.current = null;
    }

    pendingJoinRef.current = null;
    clearSessionTimeout();
    setSessionConnectionMessage('');

    if (socketRef.current) {
      socketRef.current.disconnect();

      socketRef.current = io(SERVER_URL);
      attachSocketListeners(socketRef.current, 'Reconnecte au serveur');
    }

    setJoined(false);
    locationRef.current = null;
    setLocation(null);
    setRoomUsers([]);
    setRoomDestination(null);
    setSocketId('');
    setRouteCoordinates([]);
    setRouteTarget(null);
    setRouteDistance(null);
    setRouteDuration(null);
    setRouteSteps([]);
    setRouteIsFallback(false);
    setRouteStartDistance(null);
    setRouteStartTargetId('');
    setDeviceHeading(null);
    routeRequestRef.current = { targetKey: '', origin: null, requestedAt: 0 };
    routeDurationPerMeterRef.current = null;
    cameraHeadingRef.current = null;
    walkingNearDestinationVibrationRef.current = '';
    sessionStartVoiceKeyRef.current = '';
    resetSpokenSteps();
    setActivityHistory([]);
    setIncomingSignal(null);
    setDetailsExpanded(false);
    setInviteHostPseudo('');
    setMapFollowEnabled(false);
    Speech.stop();
  };

  const socketStatusIndicatorStyle = socketOnline
    ? styles.socketStatusDotOnline
    : styles.socketStatusDotOffline;
  const socketStatusTextStyle = socketOnline
    ? styles.socketStatusValueOnline
    : styles.socketStatusValueOffline;
  const mapEngineLabel =
    Platform.OS === 'android'
      ? CAN_RENDER_NATIVE_MAP
        ? 'Native Android'
        : 'Indisponible'
      : Platform.OS === 'ios'
        ? CAN_RENDER_NATIVE_MAP
          ? 'Native iOS'
          : 'Indisponible'
        : 'Web';
  const mapEngineTextStyle =
    Platform.OS !== 'web' && CAN_RENDER_NATIVE_MAP
      ? styles.mapEngineValuePreferred
      : styles.mapEngineValueFallback;
  const updateDebugLabel = `${APP_UI_REVISION} - ${
    Updates.isEnabled ? (Updates.isEmbeddedLaunch ? 'embed' : 'OTA') : 'updates off'
  } - ${Updates.runtimeVersion || 'runtime ?'}`;
  const mapOverlayTopOffset = incomingSignal
    ? sessionConnectionMessage
      ? Math.max(insets.top + 136, 182)
      : Math.max(insets.top + 66, 112)
    : sessionConnectionMessage
      ? Math.max(insets.top + 120, 166)
      : Math.max(insets.top + 16, 58);

  const renderSocketStatus = (compact = false) => (
    <View
      style={[
        compact ? styles.socketStatusFloating : styles.socketStatusCard,
        compact ? { top: mapOverlayTopOffset } : null,
      ]}>
      <View style={styles.socketStatusHeader}>
        <View style={[styles.socketStatusDot, socketStatusIndicatorStyle]} />
        <Text style={styles.socketStatusLabel}>Socket</Text>
        <Text style={[styles.socketStatusValue, socketStatusTextStyle]}>
          {socketOnline ? 'Online' : 'Offline'}
        </Text>
      </View>
      <View style={styles.statusMetaRow}>
        <Text style={styles.statusMetaLabel}>Carte</Text>
        <Text style={[styles.statusMetaValue, mapEngineTextStyle]}>{mapEngineLabel}</Text>
      </View>
      {socketError ? <Text style={styles.socketStatusError}>{socketError}</Text> : null}
    </View>
  );

  const openAddressSearchModal = () => {
    setAddressSearchModalVisible(true);
    setMeetingAddressSearchActive(true);
    ensureMeetingSearchOrigin();
  };

  const closeAddressSearchModal = () => {
    Keyboard.dismiss();
    pendingMeetingAddressSearchRef.current = '';
    if (pendingMeetingAddressSearchTimerRef.current) {
      clearTimeout(pendingMeetingAddressSearchTimerRef.current);
      pendingMeetingAddressSearchTimerRef.current = null;
    }
    setAddressSearchModalVisible(false);
  };

  const submitMeetingAddressSearch = React.useCallback(() => {
    const trimmedQuery = meetingAddressQuery.trim();

    setMeetingAddressSearchActive(true);

    if (trimmedQuery.length < 3) {
      pendingMeetingAddressSearchRef.current = '';
      setMeetingAddressSubmittedQuery('');
      setMeetingAddressLastSearchedQuery('');
      setMeetingAddressSuggestions([]);
      setMeetingAddressLoading(false);
      setMeetingAddressError('Tape au moins 3 caracteres.');
      return;
    }

    setMeetingAddressError('');
    pendingMeetingAddressSearchRef.current = trimmedQuery;
    meetingAddressInputRef.current?.blur();
    Keyboard.dismiss();

    if (pendingMeetingAddressSearchTimerRef.current) {
      clearTimeout(pendingMeetingAddressSearchTimerRef.current);
    }

    pendingMeetingAddressSearchTimerRef.current = setTimeout(() => {
      startSubmittedMeetingAddressSearch(trimmedQuery);
    }, keyboardVisible ? IOS_KEYBOARD_SEARCH_FALLBACK_DELAY_MS : KEYBOARD_SEARCH_FALLBACK_DELAY_MS);

    ensureMeetingSearchOrigin();
  }, [
    ensureMeetingSearchOrigin,
    keyboardVisible,
    meetingAddressQuery,
    startSubmittedMeetingAddressSearch,
  ]);

  const updateMeetingAddressQuery = (nextQuery) => {
    selectedMeetingAddressQueryRef.current = '';
    addressResultsKeyboardDismissKeyRef.current = '';
    pendingMeetingAddressSearchRef.current = '';
    if (pendingMeetingAddressSearchTimerRef.current) {
      clearTimeout(pendingMeetingAddressSearchTimerRef.current);
      pendingMeetingAddressSearchTimerRef.current = null;
    }
    setMeetingAddressSubmittedQuery('');
    setMeetingAddressLastSearchedQuery('');
    setMeetingAddressSuggestions([]);
    setMeetingAddressLoading(false);
    setMeetingAddressError('');
    setMeetingAddressSearchActive(true);
    setMeetingAddressQuery(nextQuery);
  };

  const renderMeetingSuggestionItems = () =>
    meetingAddressSuggestions.map((suggestion) => (
      <TouchableOpacity
        key={suggestion.id}
        style={styles.meetingSuggestionItem}
        onPress={() => selectMeetingAddressSuggestion(suggestion)}>
        <View style={styles.meetingSuggestionTextBlock}>
          <Text style={styles.meetingSuggestionTitle} numberOfLines={1}>
            {suggestion.label}
          </Text>
          <Text style={styles.meetingSuggestionSubtitle} numberOfLines={2}>
            {suggestion.description}
          </Text>
        </View>
        <View style={styles.meetingSuggestionMeta}>
          <Text style={styles.meetingSuggestionType}>
            {suggestion.typeLabel}
          </Text>
          {suggestion.distanceLabel ? (
            <Text style={styles.meetingSuggestionDistance}>
              {suggestion.distanceLabel}
            </Text>
          ) : null}
        </View>
      </TouchableOpacity>
    ));

  const renderMeetingSuggestionContent = (compact = false, expanded = false) => {
    const trimmedQuery = meetingAddressSubmittedQuery.trim();

    if (meetingAddressLoading) {
      return (
        <View style={styles.meetingAddressStatusRow}>
          <ActivityIndicator size="small" color="#22c55e" />
          <Text style={styles.meetingAddressStatusText}>Recherche autour de toi...</Text>
        </View>
      );
    }

    if (meetingAddressError) {
      return <Text style={styles.meetingAddressError}>{meetingAddressError}</Text>;
    }

    if (meetingAddressSuggestions.length > 0) {
      const suggestionItems = renderMeetingSuggestionItems();

      return compact ? (
        <ScrollView
          style={[
            styles.meetingSuggestionScroll,
            expanded ? styles.meetingSuggestionScrollExpanded : null,
          ]}
          contentContainerStyle={styles.meetingSuggestionList}
          keyboardShouldPersistTaps="always"
          nestedScrollEnabled={true}
          showsVerticalScrollIndicator={false}>
          {suggestionItems}
        </ScrollView>
      ) : (
        <View style={styles.meetingSuggestionList}>{suggestionItems}</View>
      );
    }

    if (
      meetingAddressSearchActive &&
      trimmedQuery.length >= 3 &&
      meetingAddressLastSearchedQuery === trimmedQuery
    ) {
      return (
        <Text style={styles.meetingAddressEmptyText}>
          Aucune suggestion trouvee pour le moment
        </Text>
      );
    }

    return null;
  };

  const renderMeetingAddressSearch = (compact = false) => {
    const selectedAddressLabel = meetingAddressQuery.trim();
    const selectedAddressDescription =
      roomDestination?.description || roomDestination?.category || '';

    return (
      <View
        style={[
          styles.meetingAddressSection,
          compact ? null : styles.meetingAddressSectionHome,
        ]}>
        <Text style={styles.meetingAddressLabel}>Adresse du rendez-vous</Text>
        <TouchableOpacity
          style={styles.meetingAddressPickerButton}
          activeOpacity={0.88}
          onPress={openAddressSearchModal}>
          <Text
            style={[
              styles.meetingAddressPickerText,
              selectedAddressLabel ? null : styles.meetingAddressPickerPlaceholder,
            ]}
            numberOfLines={1}>
            {selectedAddressLabel || 'Adresse, commerce, entreprise...'}
          </Text>
          <Text style={styles.meetingAddressPickerAction}>Chercher</Text>
        </TouchableOpacity>
        {selectedAddressLabel && selectedAddressDescription ? (
          <Text style={styles.meetingAddressSelectedText} numberOfLines={2}>
            {selectedAddressDescription}
          </Text>
        ) : null}
      </View>
    );
  };

  if (joined && location) {
    const formattedDistance = formatDistance(routeDistance);
    const formattedDuration = formatDuration(routeDuration);
    const formattedArrivalTime = formatArrivalTime(routeDuration);
    const spokenStepKeySet = new Set(spokenStepKeys);
    const isMeetingMode = sessionMode === SESSION_MODE_MEETING;
    const isWalkingMode = routeTravelMode === ROUTE_TRAVEL_MODE_WALKING;
    const otherUsers = roomUsers.filter(
      (user) => hasCoordinates(user) && !isCurrentUser(user, socketId, pseudo)
    );
    const liveContactDistances = otherUsers
      .map((user) => {
        const distanceMeters = getDistanceBetweenCoordinates(location, user);

        return {
          ...user,
          distanceMeters,
          distanceLabel: formatDistance(distanceMeters),
        };
      })
      .filter(
        (user) =>
          typeof user.distanceMeters === 'number' && !!user.distanceLabel
      )
      .sort((firstUser, secondUser) => firstUser.distanceMeters - secondUser.distanceMeters);
    const nearestContact = liveContactDistances[0] || null;
    const nearestContactName = nearestContact?.pseudo || 'Contact';
    const getLiveContactDistanceLabel = (userId) =>
      liveContactDistances.find((user) => user.id === userId)?.distanceLabel || null;
    const connectedParticipants = (
      roomUsers.length
        ? roomUsers
        : [{ id: 'local-user', pseudo, invitedBy: inviteHostPseudo }]
    ).map((participant, participantIndex) => {
      const participantIsCurrent = isCurrentUser(participant, socketId, pseudo);
      const participantRelationLabel = getInviteRelationLabel(
        participantIsCurrent
          ? { ...participant, invitedBy: participant.invitedBy || inviteHostPseudo }
          : participant,
        pseudo
      );
      const participantDistanceLabel =
        liveContactDistances.find((contact) => contact.id === participant.id)
          ?.distanceLabel || '';

      return {
        id:
          participant.id ||
          `${String(participant.pseudo || 'participant')}-${participantIndex}`,
        name: participant.pseudo || (participantIsCurrent ? pseudo : 'Contact'),
        relationLabel:
          participantRelationLabel || (participantIsCurrent ? 'Toi' : 'Connecte'),
        distanceLabel: participantDistanceLabel,
      };
    });
    const meetingDistanceLabel =
      formattedDistance ||
      (roomDestination
        ? formatDistance(getDistanceBetweenCoordinates(location, roomDestination))
        : null);
    const progressTargetName = isMeetingMode
      ? roomDestination
        ? getRouteTargetLabel(roomDestination)
        : 'Rendez-vous en attente'
      : nearestContact
        ? nearestContactName
        : 'Contact en attente';
    const progressDistanceLabel = isMeetingMode
      ? meetingDistanceLabel
      : nearestContact?.distanceLabel;
    const etaLabel = formattedDuration
      ? formattedArrivalTime
        ? `${formattedDuration} - ${formattedArrivalTime}`
        : formattedDuration
      : null;
    const nextRouteStep =
      routeSteps.find((step) => !spokenStepKeySet.has(step.voiceKey)) || null;
    const pedestrianCompassTarget =
      isWalkingMode && routeTarget
        ? hasCoordinates(nextRouteStep?.location)
          ? nextRouteStep.location
          : hasCoordinates(routeCoordinates?.[1])
            ? routeCoordinates[1]
            : routeTarget
        : null;
    const pedestrianCompassBearing = pedestrianCompassTarget
      ? getBearingBetweenCoordinates(location, pedestrianCompassTarget)
      : null;
    const pedestrianHeadingDelta =
      typeof deviceHeading === 'number' && typeof pedestrianCompassBearing === 'number'
        ? getShortestHeadingDelta(deviceHeading, pedestrianCompassBearing)
        : null;
    const pedestrianDirectionLabel = getRelativeDirectionLabel(pedestrianHeadingDelta);
    const pedestrianArrowRotation = `${
      typeof pedestrianHeadingDelta === 'number' ? pedestrianHeadingDelta : 0
    }deg`;
    const pedestrianTargetLabel = isMeetingMode ? 'Rendez-vous' : progressTargetName;
    const pedestrianCompassText = progressDistanceLabel
      ? `${pedestrianTargetLabel} a ${progressDistanceLabel} ${pedestrianDirectionLabel}`
      : `${pedestrianTargetLabel} ${pedestrianDirectionLabel}`;
    const mapGuidanceTop = mapOverlayTopOffset + 50;
    const pedestrianCompassTop = nextRouteStep
      ? mapGuidanceTop + 142
      : mapGuidanceTop;
    const showMeetingSuggestionOverlay =
      isMeetingMode &&
      meetingAddressSearchActive &&
      meetingAddressQuery.trim().length >= 3;
    const arrivalDistanceMeters = isWalkingMode
      ? PEDESTRIAN_ARRIVAL_DISTANCE_METERS
      : 25;
    const progressPercentage =
      typeof routeDistance === 'number' &&
      typeof routeStartDistance === 'number' &&
      routeStartDistance > 0
        ? routeDistance <= arrivalDistanceMeters
          ? 100
          : Math.max(
              0,
              Math.min(
                100,
                Math.round(
                  ((routeStartDistance - routeDistance) / routeStartDistance) * 100
                )
              )
            )
        : 0;
    const quickMenuContent = (
      <>
        <View style={styles.simpleMenuSection}>
          <Text style={styles.simpleMenuLabel}>Code de session</Text>
          <Text style={styles.simpleMenuCode}>{code}</Text>
        </View>

        <View style={styles.simpleMenuSection}>
          <Text style={styles.simpleMenuLabel}>Connectes</Text>
          <View style={styles.simpleParticipantList}>
            {connectedParticipants.map((participant) => (
              <View key={participant.id} style={styles.simpleParticipantRow}>
                <View style={styles.simpleParticipantTextBlock}>
                  <Text style={styles.simpleParticipantName} numberOfLines={1}>
                    {participant.name}
                  </Text>
                  <Text style={styles.simpleParticipantRelation} numberOfLines={1}>
                    {participant.relationLabel}
                  </Text>
                </View>
                {participant.distanceLabel ? (
                  <Text style={styles.simpleParticipantDistance}>
                    {participant.distanceLabel}
                  </Text>
                ) : null}
              </View>
            ))}
          </View>
        </View>

        <View style={styles.simpleMenuSection}>
          <View style={styles.mapsActionsRow}>
            <TouchableOpacity
              style={styles.mapsActionButton}
              onPress={openAppleMaps}>
              <Text style={styles.mapsActionButtonText}>Apple Maps</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.mapsActionButton, styles.mapsActionButtonGoogle]}
              onPress={openGoogleMaps}>
              <Text style={styles.mapsActionButtonText}>Google Maps</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.simpleMenuSection}>
          <Text style={styles.simpleMenuLabel}>Signaux</Text>
          <View style={styles.simpleSignalRow}>
            {SIMPLE_SIGNAL_LABELS.map((signalLabel) => (
              <TouchableOpacity
                key={signalLabel}
                style={[
                  styles.simpleSignalButton,
                  signalLabel === 'Je suis arrive'
                    ? styles.simpleSignalButtonArrived
                    : null,
                ]}
                onPress={() => sendQuickSignal(signalLabel)}>
                <Text style={styles.simpleSignalButtonText}>{signalLabel}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </>
    );

    return (
      <View style={styles.container}>
        {incomingSignal ? (
          <View style={styles.signalToast}>
            <Text style={styles.signalToastText}>
              {incomingSignal.pseudo} : {incomingSignal.label}
            </Text>
          </View>
        ) : null}

        {sessionConnectionMessage ? (
          <View
            style={[
              styles.sessionToast,
              incomingSignal ? styles.sessionToastWithSignal : null,
            ]}>
            <ActivityIndicator size="small" color="#86efac" />
            <Text style={styles.sessionToastText}>{sessionConnectionMessage}</Text>
          </View>
        ) : null}

        {CAN_RENDER_NATIVE_MAP ? (
          <>
            <NativeMapView
              ref={mapRef}
              style={styles.map}
              initialRegion={{
                latitude: location.latitude,
                longitude: location.longitude,
                latitudeDelta: 0.01,
                longitudeDelta: 0.01,
              }}
              pitchEnabled={true}
              rotateEnabled={true}
              scrollEnabled={true}
              followsUserLocation={mapFollowEnabled}
              showsCompass={true}
              showsMyLocationButton={false}
              showsUserLocation={true}
              zoomEnabled={true}
              onLongPress={handleMapLongPress}>
              {routeCoordinates.length > 1 ? (
                <>
                  <NativePolyline
                    coordinates={routeCoordinates}
                    strokeColor="rgba(2, 6, 23, 0.45)"
                    strokeWidth={20}
                    lineCap="round"
                    lineJoin="round"
                  />
                  <NativePolyline
                    coordinates={routeCoordinates}
                    strokeColor="#007aff"
                    strokeWidth={15}
                    lineCap="round"
                    lineJoin="round"
                  />
                  <NativePolyline
                    coordinates={routeCoordinates}
                    strokeColor="#5ac8fa"
                    strokeWidth={6}
                    lineCap="round"
                    lineJoin="round"
                  />
                </>
              ) : null}

              {roomDestination && hasCoordinates(roomDestination) ? (
                <NativeMarker
                  coordinate={{
                    latitude: roomDestination.latitude,
                    longitude: roomDestination.longitude,
                  }}
                  title={roomDestination.label || 'Rendez-vous'}
                  description={roomDestination.description || 'Point de rendez-vous'}
                  anchor={{ x: 0.5, y: 0.5 }}>
                  <View style={styles.meetingMapMarker}>
                    <Text style={styles.meetingMapMarkerText}>RDV</Text>
                  </View>
                </NativeMarker>
              ) : null}

              {otherUsers.map((user) => {
                const contactDistanceLabel = getLiveContactDistanceLabel(user.id);
                const contactInviteLabel = getInviteRelationLabel(user, pseudo);
                const contactDescription =
                  [contactInviteLabel, contactDistanceLabel].filter(Boolean).join(' - ') ||
                  `Code : ${code}`;

                return (
                  <NativeMarker
                    key={user.id}
                    coordinate={{
                      latitude: user.latitude,
                      longitude: user.longitude,
                    }}
                    title={user.pseudo}
                    description={contactDescription}
                    anchor={{ x: 0.5, y: 1 }}>
                    <View style={styles.mapMarkerContainer}>
                      <View
                        style={[
                          styles.mapMarkerLabel,
                          routeTarget?.id === user.id ? styles.mapMarkerLabelTarget : null,
                        ]}>
                        <Text style={styles.mapMarkerLabelText} numberOfLines={1}>
                          {user.pseudo || 'Contact'}
                        </Text>
                        {contactInviteLabel ? (
                          <Text style={styles.mapMarkerInviteText} numberOfLines={1}>
                            {contactInviteLabel}
                          </Text>
                        ) : null}
                        {contactDistanceLabel ? (
                          <Text style={styles.mapMarkerDistanceText} numberOfLines={1}>
                            {contactDistanceLabel}
                          </Text>
                        ) : null}
                      </View>
                      <View
                        style={[
                          styles.mapMarkerDot,
                          routeTarget?.id === user.id ? styles.mapMarkerDotTarget : null,
                        ]}
                      />
                    </View>
                  </NativeMarker>
                );
              })}
            </NativeMapView>

            {nextRouteStep ? (
              <TouchableOpacity
                style={[styles.mapGuidanceCard, { top: mapGuidanceTop }]}
                onPress={() => speakRouteStep(nextRouteStep, 'Prochaine etape. ')}>
                <Text style={styles.mapGuidanceLabel}>Prochaine etape</Text>
                <Text style={styles.mapGuidanceInstruction} numberOfLines={2}>
                  {nextRouteStep.instruction}
                </Text>
                <Text style={styles.mapGuidanceMeta}>
                  {nextRouteStep.distanceLabel || 'Etape proche'}
                  {nextRouteStep.durationLabel ? ` - ${nextRouteStep.durationLabel}` : ''}
                </Text>
              </TouchableOpacity>
            ) : null}

            {isWalkingMode && routeTarget ? (
              <TouchableOpacity
                style={[styles.mapPedestrianCompassCard, { top: pedestrianCompassTop }]}
                onPress={() =>
                  nextRouteStep
                    ? speakRouteStep(nextRouteStep, 'Prochaine etape. ')
                    : Vibration.vibrate(60)
                }>
                <View style={styles.pedestrianCompassArrowShell}>
                  <View
                    style={[
                      styles.pedestrianCompassArrow,
                      { transform: [{ rotate: pedestrianArrowRotation }] },
                    ]}>
                    <View style={styles.pedestrianCompassArrowHead} />
                    <View style={styles.pedestrianCompassArrowStem} />
                  </View>
                </View>
                <View style={styles.mapPedestrianCompassTextBlock}>
                  <Text style={styles.pedestrianCompassLabel}>Boussole pieton</Text>
                  <Text style={styles.pedestrianCompassText} numberOfLines={2}>
                    {pedestrianCompassText}
                  </Text>
                </View>
              </TouchableOpacity>
            ) : null}

          </>
        ) : (
          <View style={styles.webMapFallback}>
            {Platform.OS === 'android' ? (
              <>
                <Text style={styles.webMapFallbackTitle}>
                  Carte Android indisponible dans cette build
                </Text>
                <Text style={styles.webMapFallbackText}>
                  Cette build ne fournit pas le module natif de carte attendu. L&apos;app
                  reste ouverte et tu peux continuer la session, puis ouvrir
                  l&apos;itineraire dans Google Maps depuis les actions du bas.
                </Text>
                <Text style={styles.webMapFallbackText}>
                  Pour reactiver la carte integree, rebuild l&apos;application Android
                  avec react-native-maps.
                </Text>
              </>
            ) : (
              <>
                <Text style={styles.webMapFallbackTitle}>Carte indisponible sur le web</Text>
                <Text style={styles.webMapFallbackText}>
                  Rejoins la session depuis l&apos;app iPhone ou Android pour voir la
                  position en temps reel, le trajet et la progression.
                </Text>
              </>
            )}
          </View>
        )}

        {showMeetingSuggestionOverlay ? (
          <View style={styles.mapSuggestionOverlay}>
            <View style={styles.mapSuggestionHeader}>
              <Text style={styles.mapSuggestionTitle}>Suggestions</Text>
              <TouchableOpacity onPress={() => setMeetingAddressSearchActive(false)}>
                <Text style={styles.mapSuggestionClose}>Fermer</Text>
              </TouchableOpacity>
            </View>
            {renderMeetingSuggestionContent(true)}
          </View>
        ) : null}

        <TouchableOpacity
          style={[styles.mapCloseButton, { top: Math.max(insets.top + 8, 46) }]}
          onPress={stopSharing}>
          <Text style={styles.mapCloseButtonText}>Fermer</Text>
        </TouchableOpacity>

        <View style={[styles.bottomDock, { bottom: Math.max(insets.bottom + 10, 16) }]}>
          {detailsExpanded ? (
            <View style={styles.quickMenuPanel}>
              <ScrollView
                style={styles.quickMenuScroll}
                contentContainerStyle={styles.quickMenuContent}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled={true}
                showsVerticalScrollIndicator={false}>
                {quickMenuContent}
              </ScrollView>
            </View>
          ) : null}

          <View
            style={[
              styles.bottomCard,
              styles.bottomCardCollapsed,
            ]}>
            <View style={styles.progressSection}>
              <View style={styles.progressHeader}>
                <View style={styles.progressTitleBlock}>
                  <Text style={styles.progressLabel}>Progression</Text>
                  <Text style={styles.progressContactName} numberOfLines={1}>
                    {progressTargetName}
                  </Text>
                </View>
                <Text style={styles.progressValue}>{progressPercentage}%</Text>
              </View>
              <View style={styles.progressTrackRow}>
                <View style={styles.progressTrack}>
                  <View
                    style={[
                      styles.progressFill,
                      {
                        width: `${progressPercentage}%`,
                      },
                    ]}
                  />
                </View>
                <View style={styles.liveDistancePill}>
                  <Text
                    style={[
                      styles.liveDistancePillText,
                      progressDistanceLabel ? null : styles.liveDistancePillEmpty,
                    ]}
                    numberOfLines={1}>
                    {progressDistanceLabel || '--'}
                  </Text>
                </View>
              </View>
              <View style={styles.etaRow}>
                <View style={styles.etaMetric}>
                  <Text style={styles.etaLabel}>Distance</Text>
                  <Text style={styles.etaValue}>{progressDistanceLabel || '--'}</Text>
                </View>
                <View style={styles.etaDivider} />
                <View style={styles.etaMetric}>
                  <Text style={styles.etaLabel}>Arrivee estimee</Text>
                  <Text style={styles.etaValue}>{etaLabel || '--'}</Text>
                </View>
              </View>
            </View>

            <View style={styles.mapControlsPanel}>
              <View style={styles.routeTravelSwitch}>
                <TouchableOpacity
                  style={[
                    styles.routeTravelButton,
                    routeTravelMode === ROUTE_TRAVEL_MODE_DRIVING
                      ? styles.routeTravelButtonActive
                      : null,
                  ]}
                  onPress={() => updateRouteTravelMode(ROUTE_TRAVEL_MODE_DRIVING)}>
                  <Text
                    style={[
                      styles.routeTravelButtonText,
                      routeTravelMode === ROUTE_TRAVEL_MODE_DRIVING
                        ? styles.routeTravelButtonTextActive
                        : null,
                    ]}>
                    Voiture
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.routeTravelButton,
                    routeTravelMode === ROUTE_TRAVEL_MODE_WALKING
                      ? styles.routeTravelButtonActive
                      : null,
                  ]}
                  onPress={() => updateRouteTravelMode(ROUTE_TRAVEL_MODE_WALKING)}>
                  <Text
                    style={[
                      styles.routeTravelButtonText,
                      routeTravelMode === ROUTE_TRAVEL_MODE_WALKING
                        ? styles.routeTravelButtonTextActive
                        : null,
                    ]}>
                    A pied
                  </Text>
                </TouchableOpacity>
              </View>

              <View style={styles.mapControlsRow}>
                <View style={styles.mapModeSwitch}>
                  <TouchableOpacity
                    style={[
                      styles.mapModeButton,
                      mapFollowEnabled ? styles.mapModeButtonActive : null,
                    ]}
                    onPress={enableAutomaticMapMode}>
                    <Text
                      style={[
                        styles.mapModeButtonText,
                        mapFollowEnabled ? styles.mapModeButtonTextActive : null,
                      ]}>
                      Auto
                    </Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[
                      styles.mapModeButton,
                      !mapFollowEnabled ? styles.mapModeButtonActive : null,
                    ]}
                    onPress={enableManualMapMode}>
                    <Text
                      style={[
                        styles.mapModeButtonText,
                        !mapFollowEnabled ? styles.mapModeButtonTextActive : null,
                      ]}>
                      Manuel
                    </Text>
                  </TouchableOpacity>
                </View>

                <TouchableOpacity
                  style={[
                    styles.recenterButton,
                    mapFollowEnabled ? styles.recenterButtonFollowing : null,
                  ]}
                  onPress={recenterMapOnUser}>
                  <Text style={styles.recenterButtonText}>Recentrer</Text>
                </TouchableOpacity>
              </View>

            </View>
          </View>

          <TouchableOpacity
            style={styles.floatingToggleButton}
            onPress={() => setDetailsExpanded((currentValue) => !currentValue)}>
            <Text style={styles.floatingToggleButtonText}>
              {detailsExpanded ? '-' : '+'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.homeRoot}>
      <Modal
        animationType="slide"
        presentationStyle="fullScreen"
        visible={addressSearchModalVisible}
        onRequestClose={closeAddressSearchModal}>
        <KeyboardAvoidingView
          style={styles.addressModalRoot}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View
            style={[
              styles.addressModalHeader,
              { paddingTop: Math.max(insets.top + 14, 34) },
            ]}>
            <TouchableOpacity
              style={styles.addressModalHeaderButton}
              onPress={closeAddressSearchModal}>
              <Text style={styles.addressModalHeaderButtonText}>Fermer</Text>
            </TouchableOpacity>
            <Text style={styles.addressModalTitle}>Adresse du rendez-vous</Text>
            <TouchableOpacity
              style={styles.addressModalHeaderButton}
              onPress={() => {
                if (keyboardVisible) {
                  Keyboard.dismiss();
                } else {
                  meetingAddressInputRef.current?.focus();
                }
              }}>
              <Text style={styles.addressModalHeaderButtonText}>
                {keyboardVisible ? 'Masquer' : 'Clavier'}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.addressModalContent}>
            <Text style={styles.addressModalLabel}>
              Tape une adresse, un commerce ou une entreprise
            </Text>
            <TextInput
              ref={meetingAddressInputRef}
              style={styles.addressModalInput}
              placeholder="Ex : 3 rue du Rouet, boulangerie..."
              placeholderTextColor="#94a3b8"
              value={meetingAddressQuery}
              onChangeText={updateMeetingAddressQuery}
              onFocus={() => {
                setMeetingAddressSearchActive(true);
                ensureMeetingSearchOrigin();
              }}
              onSubmitEditing={submitMeetingAddressSearch}
              autoCorrect={false}
              clearButtonMode="while-editing"
              returnKeyType="search"
            />

            <View style={styles.addressModalActionRow}>
              <TouchableOpacity
                style={styles.addressModalSearchButton}
                onPress={submitMeetingAddressSearch}>
                <Text style={styles.addressModalSearchButtonText}>
                  {meetingAddressLoading ? 'Recherche...' : 'Rechercher'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.addressModalClearButton}
                onPress={() => {
                  addressResultsKeyboardDismissKeyRef.current = '';
                  pendingMeetingAddressSearchRef.current = '';
                  if (pendingMeetingAddressSearchTimerRef.current) {
                    clearTimeout(pendingMeetingAddressSearchTimerRef.current);
                    pendingMeetingAddressSearchTimerRef.current = null;
                  }
                  setMeetingAddressQuery('');
                  setMeetingAddressSubmittedQuery('');
                  setMeetingAddressSuggestions([]);
                  setMeetingAddressLoading(false);
                  setMeetingAddressError('');
                  setMeetingAddressLastSearchedQuery('');
                  meetingAddressInputRef.current?.focus();
                }}>
                <Text style={styles.addressModalClearButtonText}>Effacer</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.addressModalResults}>
              {renderMeetingSuggestionContent(true, true) || (
                <Text style={styles.addressModalHint}>
                  Tape au moins 3 caracteres puis appuie sur Rechercher.
                </Text>
              )}
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      <Modal
        animationType="fade"
        transparent={true}
        visible={invitePromptVisible}
        onRequestClose={closeInvitePrompt}>
        <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
          <View style={styles.invitePromptOverlay}>
            <View style={styles.invitePromptCard}>
              <Text style={styles.invitePromptTitle}>Nom du contact</Text>
              <Text style={styles.invitePromptText}>
                Choisis le prenom ou surnom qui sera affiche sur sa carte.
              </Text>
              <TextInput
                style={styles.invitePromptInput}
                placeholder="Ex : Marie"
                placeholderTextColor="#94a3b8"
                value={inviteGuestPseudo}
                onChangeText={(nextValue) => {
                  setInviteGuestPseudo(nextValue);
                  setInvitePromptError('');
                }}
                autoFocus={true}
                returnKeyType="send"
                onSubmitEditing={confirmInviteShare}
              />
              {invitePromptError ? (
                <Text style={styles.invitePromptError}>{invitePromptError}</Text>
              ) : null}
              <View style={styles.invitePromptActions}>
                <TouchableOpacity
                  style={[styles.invitePromptButton, styles.invitePromptCancelButton]}
                  onPress={closeInvitePrompt}>
                  <Text style={styles.invitePromptCancelText}>Annuler</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.invitePromptButton, styles.invitePromptConfirmButton]}
                  onPress={confirmInviteShare}>
                  <Text style={styles.invitePromptConfirmText}>
                    {phone.trim() ? 'SMS' : 'Partager'}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      <KeyboardAvoidingView
        style={styles.homeKeyboardContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 28 : 0}>
        <ScrollView
          style={styles.homeContainer}
          contentContainerStyle={[
            styles.homeContent,
            {
              paddingTop: Math.max(insets.top + 18, 56),
              paddingBottom: Math.max(insets.bottom + 120, 120),
            },
          ]}
          keyboardShouldPersistTaps="always"
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
          scrollEventThrottle={16}
          alwaysBounceVertical={true}
          showsVerticalScrollIndicator={false}>
          <Text style={styles.title}>jarrive</Text>
          <Text style={styles.uiRevisionBadge} numberOfLines={1}>
            {updateDebugLabel}
          </Text>
          <Text style={styles.subtitle}>
            Retrouve quelqu&apos;un avec un code de session temporaire
          </Text>
          <Text style={styles.helperText}>
            Aucun compte n&apos;est requis. Entre ton pseudo puis colle ou genere un code.
          </Text>
          <Text style={styles.helperText}>
            A plusieurs, chacun met son propre prenom pour se reconnaitre sur la carte.
          </Text>
          <Text style={styles.helperText}>
            Ta position est utilisee uniquement pendant l&apos;utilisation de l&apos;app pour
            vous afficher sur la carte en temps reel.
          </Text>
          <Text style={styles.helperText}>
            Ajoute un numero de telephone si tu veux ouvrir un SMS avec un lien deja rempli.
          </Text>

          {renderSocketStatus(false)}

          <View style={styles.modeSwitch}>
            <TouchableOpacity
              style={[
                styles.modeButton,
                sessionMode === SESSION_MODE_CONTACT ? styles.modeButtonActive : null,
              ]}
              onPress={() => setSessionMode(SESSION_MODE_CONTACT)}>
              <Text
                style={[
                  styles.modeButtonText,
                  sessionMode === SESSION_MODE_CONTACT
                    ? styles.modeButtonTextActive
                    : null,
                ]}>
                Rejoindre
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.modeButton,
                sessionMode === SESSION_MODE_MEETING ? styles.modeButtonActive : null,
              ]}
              onPress={() => setSessionMode(SESSION_MODE_MEETING)}>
              <Text
                style={[
                  styles.modeButtonText,
                  sessionMode === SESSION_MODE_MEETING
                    ? styles.modeButtonTextActive
                    : null,
                ]}>
                Rendez-vous
              </Text>
            </TouchableOpacity>
          </View>

          <TextInput
            style={styles.input}
            placeholder="Ton prenom ou surnom"
            placeholderTextColor="#94a3b8"
            value={pseudo}
            onChangeText={setPseudo}
            returnKeyType="next"
          />

          <View style={styles.inputRow}>
            <TextInput
              style={[styles.input, styles.rowInput]}
              placeholder="Code de session"
              placeholderTextColor="#94a3b8"
              value={code}
              onChangeText={(nextCode) => {
                setCode(nextCode);
                setInviteHostPseudo('');
              }}
              returnKeyType="done"
            />

            <TouchableOpacity
              style={[styles.pasteButton, styles.generateCodeButton]}
              onPress={generateNewCode}>
              <Text style={styles.pasteButtonText}>Generer</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.pasteButton} onPress={pasteCodeFromClipboard}>
              <Text style={styles.pasteButtonText}>Coller</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.phoneFieldShell}>
            <Text style={styles.phoneFieldLabel}>Numero de telephone</Text>
            <View style={styles.phoneFieldRow}>
              <TextInput
                style={styles.phoneFieldInput}
                placeholder="Ex : 06 12 34 56 78"
                placeholderTextColor="#94a3b8"
                value={phone}
                onChangeText={setPhone}
                keyboardType="phone-pad"
                textContentType="telephoneNumber"
                autoComplete="tel"
              />

              <TouchableOpacity
                style={styles.phonePasteButton}
                onPress={pastePhoneFromClipboard}>
                <Text style={styles.phonePasteButtonText}>Coller</Text>
              </TouchableOpacity>
            </View>
          </View>

          {sessionMode === SESSION_MODE_MEETING ? renderMeetingAddressSearch() : null}

          {keyboardVisible ? (
            <TouchableOpacity
              style={styles.keyboardDismissButton}
              onPress={Keyboard.dismiss}>
              <Text style={styles.keyboardDismissButtonText}>Masquer le clavier</Text>
            </TouchableOpacity>
          ) : null}

          <TouchableOpacity style={styles.button} onPress={getLocationAndJoin}>
            <Text style={styles.buttonText}>
              {sessionMode === SESSION_MODE_MEETING
                ? 'Entrer dans le rendez-vous'
                : 'Rejoindre la session'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.secondaryButton} onPress={shareCode}>
            <Text style={styles.buttonText}>
              {sessionMode === SESSION_MODE_MEETING
                ? phone.trim()
                  ? 'Envoyer le rendez-vous par SMS'
                  : 'Partager le lien de rendez-vous'
                : phone.trim()
                  ? 'Envoyer le lien par SMS'
                  : 'Partager mon lien de session'}
            </Text>
          </TouchableOpacity>
          <Text style={styles.uiRevisionText} numberOfLines={1}>
            {updateDebugLabel}
          </Text>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0f172a',
  },
  homeRoot: {
    flex: 1,
    backgroundColor: '#0f172a',
  },
  homeContainer: {
    flex: 1,
    backgroundColor: '#0f172a',
  },
  homeKeyboardContainer: {
    flex: 1,
    backgroundColor: '#0f172a',
  },
  homeContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    paddingHorizontal: 24,
    paddingTop: 34,
    paddingBottom: 120,
  },
  addressModalRoot: {
    flex: 1,
    backgroundColor: '#0f172a',
  },
  addressModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingHorizontal: 16,
    paddingBottom: 12,
    backgroundColor: '#0f172a',
    borderBottomWidth: 1,
    borderBottomColor: '#1e293b',
  },
  addressModalHeaderButton: {
    minWidth: 72,
    borderRadius: 999,
    backgroundColor: '#1e293b',
    paddingHorizontal: 12,
    paddingVertical: 9,
    alignItems: 'center',
  },
  addressModalHeaderButtonText: {
    color: '#f8fafc',
    fontSize: 13,
    fontWeight: '800',
  },
  addressModalTitle: {
    flex: 1,
    color: '#f8fafc',
    fontSize: 16,
    fontWeight: '900',
    textAlign: 'center',
  },
  addressModalContent: {
    flex: 1,
    padding: 18,
  },
  addressModalLabel: {
    color: '#fdba74',
    fontSize: 13,
    fontWeight: '900',
    marginBottom: 10,
  },
  addressModalInput: {
    backgroundColor: '#1e293b',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#f97316',
    color: '#fff',
    fontSize: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  addressModalActionRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
    marginTop: 10,
  },
  addressModalSearchButton: {
    flex: 1,
    backgroundColor: '#2563eb',
    borderRadius: 14,
    paddingVertical: 12,
    alignItems: 'center',
  },
  addressModalSearchButtonText: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '900',
  },
  addressModalClearButton: {
    backgroundColor: '#1e293b',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#334155',
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
  },
  addressModalClearButtonText: {
    color: '#cbd5e1',
    fontSize: 14,
    fontWeight: '900',
  },
  addressModalResults: {
    flex: 1,
    marginTop: 12,
  },
  addressModalHint: {
    color: '#94a3b8',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
  },
  title: {
    fontSize: 34,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
    marginBottom: 4,
  },
  uiRevisionBadge: {
    alignSelf: 'center',
    maxWidth: '100%',
    color: '#64748b',
    fontSize: 11,
    fontWeight: '800',
    marginBottom: 12,
  },
  uiRevisionText: {
    alignSelf: 'center',
    maxWidth: '100%',
    color: '#64748b',
    fontSize: 11,
    fontWeight: '800',
    marginTop: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#cbd5e1',
    textAlign: 'center',
    marginBottom: 12,
  },
  helperText: {
    fontSize: 14,
    color: '#94a3b8',
    textAlign: 'center',
    marginBottom: 10,
    lineHeight: 20,
  },
  invitePromptOverlay: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    padding: 24,
  },
  invitePromptCard: {
    backgroundColor: '#111827',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#334155',
    padding: 18,
  },
  invitePromptTitle: {
    color: '#f8fafc',
    fontSize: 20,
    fontWeight: '800',
    marginBottom: 8,
  },
  invitePromptText: {
    color: '#cbd5e1',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 14,
  },
  invitePromptInput: {
    backgroundColor: '#1e293b',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#334155',
    color: '#fff',
    fontSize: 16,
    paddingHorizontal: 16,
    paddingVertical: 13,
  },
  invitePromptError: {
    color: '#fca5a5',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 8,
  },
  invitePromptActions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
  },
  invitePromptButton: {
    flex: 1,
    borderRadius: 14,
    paddingVertical: 13,
    alignItems: 'center',
  },
  invitePromptCancelButton: {
    backgroundColor: '#334155',
  },
  invitePromptConfirmButton: {
    backgroundColor: '#22c55e',
  },
  invitePromptCancelText: {
    color: '#f8fafc',
    fontSize: 15,
    fontWeight: '800',
  },
  invitePromptConfirmText: {
    color: '#06210f',
    fontSize: 15,
    fontWeight: '800',
  },
  modeSwitch: {
    flexDirection: 'row',
    backgroundColor: '#111827',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#334155',
    padding: 4,
    marginBottom: 14,
    gap: 4,
  },
  modeButton: {
    flex: 1,
    borderRadius: 12,
    paddingVertical: 11,
    alignItems: 'center',
  },
  modeButtonActive: {
    backgroundColor: '#22c55e',
  },
  modeButtonText: {
    color: '#cbd5e1',
    fontSize: 14,
    fontWeight: '800',
  },
  modeButtonTextActive: {
    color: '#06210f',
  },
  input: {
    backgroundColor: '#1e293b',
    color: '#fff',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 14,
    fontSize: 16,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
  },
  rowInput: {
    flex: 1,
    marginBottom: 14,
  },
  phoneFieldShell: {
    backgroundColor: '#111827',
    borderWidth: 1.5,
    borderColor: '#3b82f6',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingTop: 12,
    paddingBottom: 14,
    marginBottom: 14,
  },
  phoneFieldLabel: {
    color: '#93c5fd',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 10,
    textTransform: 'uppercase',
  },
  phoneFieldRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  phoneFieldInput: {
    flex: 1,
    backgroundColor: '#1e293b',
    color: '#fff',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#334155',
  },
  phonePasteButton: {
    backgroundColor: '#2563eb',
    borderRadius: 14,
    paddingHorizontal: 16,
    paddingVertical: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  phonePasteButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  keyboardDismissButton: {
    alignSelf: 'center',
    backgroundColor: '#1d4ed8',
    borderRadius: 999,
    paddingHorizontal: 18,
    paddingVertical: 10,
    marginBottom: 14,
  },
  keyboardDismissButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  button: {
    backgroundColor: '#22c55e',
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: 'center',
    marginBottom: 12,
  },
  secondaryButton: {
    backgroundColor: '#3b82f6',
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: 'center',
  },
  pasteButton: {
    backgroundColor: '#334155',
    borderRadius: 14,
    paddingHorizontal: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  generateCodeButton: {
    backgroundColor: '#2563eb',
  },
  pasteButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  map: {
    flex: 1,
  },
  mapGuidanceCard: {
    position: 'absolute',
    left: 20,
    right: 20,
    zIndex: 3,
    elevation: 8,
    backgroundColor: 'rgba(15, 23, 42, 0.94)',
    borderWidth: 1,
    borderColor: '#22c55e',
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 12,
    shadowColor: '#000',
    shadowOpacity: 0.22,
    shadowRadius: 8,
    shadowOffset: {
      width: 0,
      height: 3,
    },
  },
  mapGuidanceLabel: {
    color: '#86efac',
    fontSize: 12,
    fontWeight: '800',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  mapGuidanceInstruction: {
    color: '#f8fafc',
    fontSize: 16,
    fontWeight: '800',
    lineHeight: 21,
  },
  mapGuidanceMeta: {
    color: '#cbd5e1',
    fontSize: 13,
    fontWeight: '700',
    marginTop: 4,
  },
  mapPedestrianCompassCard: {
    position: 'absolute',
    left: 18,
    right: 18,
    zIndex: 4,
    elevation: 9,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(15, 23, 42, 0.9)',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#22c55e',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 12,
    shadowColor: '#000',
    shadowOpacity: 0.22,
    shadowRadius: 8,
    shadowOffset: {
      width: 0,
      height: 3,
    },
  },
  mapPedestrianCompassTextBlock: {
    flex: 1,
  },
  mapCloseButton: {
    position: 'absolute',
    top: 46,
    left: 18,
    zIndex: 6,
    elevation: 10,
    backgroundColor: 'rgba(15, 23, 42, 0.94)',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#ef4444',
    paddingHorizontal: 16,
    paddingVertical: 10,
    shadowColor: '#000',
    shadowOpacity: 0.22,
    shadowRadius: 8,
    shadowOffset: {
      width: 0,
      height: 3,
    },
  },
  mapCloseButtonText: {
    color: '#f8fafc',
    fontSize: 13,
    fontWeight: '900',
  },
  mapSuggestionOverlay: {
    position: 'absolute',
    left: 18,
    right: 18,
    bottom: 292,
    zIndex: 7,
    elevation: 11,
    maxHeight: 250,
    backgroundColor: 'rgba(15, 23, 42, 0.96)',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#f97316',
    padding: 12,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 10,
    shadowOffset: {
      width: 0,
      height: 4,
    },
  },
  homeSuggestionOverlay: {
    position: 'absolute',
    left: 24,
    right: 24,
    zIndex: 20,
    elevation: 14,
    maxHeight: 250,
    backgroundColor: 'rgba(15, 23, 42, 0.98)',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#f97316',
    padding: 12,
    shadowColor: '#000',
    shadowOpacity: 0.28,
    shadowRadius: 12,
    shadowOffset: {
      width: 0,
      height: 5,
    },
  },
  mapSuggestionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  mapSuggestionTitle: {
    color: '#fdba74',
    fontSize: 13,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  mapSuggestionClose: {
    color: '#f8fafc',
    fontSize: 12,
    fontWeight: '800',
  },
  mapControlsPanel: {
    gap: 8,
    marginTop: 8,
    marginBottom: 2,
  },
  mapControlsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 8,
  },
  routeTravelSwitch: {
    flexDirection: 'row',
    backgroundColor: 'rgba(15, 23, 42, 0.94)',
    borderWidth: 1,
    borderColor: '#475569',
    borderRadius: 999,
    padding: 3,
  },
  routeTravelButton: {
    flex: 1,
    height: 34,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  routeTravelButtonActive: {
    backgroundColor: '#f97316',
  },
  routeTravelButtonText: {
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: '800',
  },
  routeTravelButtonTextActive: {
    color: '#111827',
  },
  mapModeSwitch: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: 'rgba(15, 23, 42, 0.94)',
    borderWidth: 1,
    borderColor: '#22c55e',
    borderRadius: 999,
    padding: 3,
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: {
      width: 0,
      height: 3,
    },
  },
  mapModeButton: {
    flex: 1,
    height: 34,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapModeButtonActive: {
    backgroundColor: '#22c55e',
  },
  mapModeButtonText: {
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: '800',
  },
  mapModeButtonTextActive: {
    color: '#052e16',
  },
  recenterButton: {
    backgroundColor: '#0f172a',
    borderWidth: 1,
    borderColor: '#475569',
    borderRadius: 999,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: {
      width: 0,
      height: 3,
    },
  },
  recenterButtonFollowing: {
    borderColor: '#22c55e',
  },
  recenterButtonText: {
    color: '#f8fafc',
    fontSize: 13,
    fontWeight: '800',
  },
  webMapFallback: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 28,
    backgroundColor: '#0f172a',
  },
  webMapFallbackTitle: {
    color: '#f8fafc',
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 10,
  },
  webMapFallbackText: {
    color: '#cbd5e1',
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 340,
  },
  androidMarkerContainer: {
    alignItems: 'center',
  },
  androidMarkerBubble: {
    maxWidth: 144,
    backgroundColor: '#0f172a',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1.5,
    borderColor: '#38bdf8',
  },
  androidMarkerBubbleSelf: {
    backgroundColor: '#1d4ed8',
    borderColor: '#93c5fd',
  },
  androidMarkerBubbleTarget: {
    backgroundColor: '#14532d',
    borderColor: '#86efac',
  },
  androidMarkerText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  androidMarkerDot: {
    marginTop: 6,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#38bdf8',
    borderWidth: 2,
    borderColor: '#e0f2fe',
  },
  androidMarkerDotSelf: {
    backgroundColor: '#bfdbfe',
    borderColor: '#1d4ed8',
  },
  androidMarkerDotTarget: {
    backgroundColor: '#22c55e',
    borderColor: '#14532d',
  },
  mapMarkerContainer: {
    alignItems: 'center',
  },
  mapMarkerLabel: {
    maxWidth: 150,
    backgroundColor: '#0f172a',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#38bdf8',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  mapMarkerLabelTarget: {
    backgroundColor: '#14532d',
    borderColor: '#86efac',
  },
  mapMarkerLabelMeeting: {
    backgroundColor: '#431407',
    borderColor: '#fdba74',
  },
  mapMarkerLabelText: {
    color: '#f8fafc',
    fontSize: 12,
    fontWeight: '800',
    textAlign: 'center',
  },
  mapMarkerInviteText: {
    color: '#bae6fd',
    fontSize: 10,
    fontWeight: '700',
    marginTop: 2,
    textAlign: 'center',
  },
  mapMarkerDistanceText: {
    color: '#cbd5e1',
    fontSize: 10,
    fontWeight: '700',
    marginTop: 2,
    textAlign: 'center',
  },
  mapMarkerDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#38bdf8',
    borderWidth: 2,
    borderColor: '#e0f2fe',
    marginTop: 5,
  },
  mapMarkerDotTarget: {
    backgroundColor: '#22c55e',
    borderColor: '#14532d',
  },
  mapMarkerDotMeeting: {
    backgroundColor: '#f97316',
    borderColor: '#431407',
  },
  meetingMapMarker: {
    minWidth: 34,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(249, 115, 22, 0.92)',
    borderWidth: 2,
    borderColor: '#fff7ed',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 7,
  },
  meetingMapMarkerText: {
    color: '#431407',
    fontSize: 10,
    fontWeight: '900',
  },
  bottomDock: {
    position: 'absolute',
    left: 14,
    right: 14,
    bottom: 16,
  },
  quickMenuPanel: {
    maxHeight: 286,
    backgroundColor: 'rgba(15, 23, 42, 0.98)',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#22c55e',
    padding: 10,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOpacity: 0.22,
    shadowRadius: 10,
    shadowOffset: {
      width: 0,
      height: 4,
    },
    elevation: 8,
  },
  quickMenuScroll: {
    maxHeight: 266,
  },
  quickMenuContent: {
    paddingBottom: 2,
  },
  bottomCard: {
    backgroundColor: '#1e293b',
    padding: 12,
    borderRadius: 18,
    paddingBottom: 12,
  },
  bottomCardExpanded: {
    maxHeight: '38%',
    overflow: 'hidden',
  },
  bottomCardCollapsed: {
    paddingTop: 14,
    paddingBottom: 14,
  },
  bottomDetailsScroll: {
    flexShrink: 1,
    marginTop: 4,
    maxHeight: 210,
  },
  bottomDetailsContent: {
    paddingBottom: 4,
  },
  simpleMenuSection: {
    backgroundColor: '#0f172a',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#334155',
    padding: 12,
    marginBottom: 10,
  },
  simpleMenuLabel: {
    color: '#86efac',
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
    marginBottom: 7,
  },
  simpleMenuCode: {
    color: '#f8fafc',
    fontSize: 22,
    fontWeight: '900',
    letterSpacing: 0,
  },
  simpleParticipantList: {
    gap: 8,
  },
  simpleParticipantRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    backgroundColor: '#111827',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  simpleParticipantTextBlock: {
    flex: 1,
  },
  simpleParticipantName: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '800',
  },
  simpleParticipantRelation: {
    color: '#94a3b8',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 2,
  },
  simpleParticipantDistance: {
    color: '#86efac',
    fontSize: 13,
    fontWeight: '900',
  },
  simpleSignalRow: {
    flexDirection: 'row',
    gap: 10,
  },
  simpleSignalButton: {
    flex: 1,
    backgroundColor: '#334155',
    borderRadius: 12,
    paddingVertical: 11,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#475569',
  },
  simpleSignalButtonArrived: {
    backgroundColor: '#14532d',
    borderColor: '#22c55e',
  },
  simpleSignalButtonText: {
    color: '#f8fafc',
    fontSize: 13,
    fontWeight: '900',
  },
  sessionSummaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 4,
    marginBottom: 8,
  },
  sessionSummaryBlock: {
    flex: 1,
  },
  sessionSummaryLabel: {
    color: '#86efac',
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  sessionSummaryTitle: {
    color: '#f8fafc',
    fontSize: 17,
    fontWeight: '800',
  },
  sessionCodePill: {
    backgroundColor: '#0f172a',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#475569',
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  sessionCodeText: {
    color: '#e2e8f0',
    fontSize: 13,
    fontWeight: '800',
  },
  sessionMetaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 6,
  },
  sessionMetaPill: {
    backgroundColor: '#334155',
    borderRadius: 999,
    color: '#f8fafc',
    fontSize: 12,
    fontWeight: '700',
    paddingHorizontal: 10,
    paddingVertical: 6,
    overflow: 'hidden',
  },
  sessionRelationText: {
    color: '#94a3b8',
    fontSize: 12,
    fontWeight: '700',
    marginBottom: 8,
  },
  cardTitle: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  cardText: {
    color: '#cbd5e1',
    fontSize: 14,
    marginBottom: 6,
  },
  routeText: {
    color: '#86efac',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 6,
  },
  routeMetricsRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    marginBottom: 10,
  },
  routeMetricPill: {
    flex: 1,
    backgroundColor: '#0f172a',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  routeMetricLabel: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: '800',
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  routeMetricValue: {
    color: '#f8fafc',
    fontSize: 13,
    fontWeight: '800',
  },
  meetingActionsSection: {
    backgroundColor: '#0f172a',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#334155',
    marginTop: 10,
    marginBottom: 10,
    padding: 10,
  },
  meetingAddressSection: {
    backgroundColor: '#111827',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#334155',
    marginBottom: 10,
    padding: 12,
  },
  meetingAddressSectionHome: {
    borderColor: '#f97316',
    marginBottom: 14,
  },
  meetingAddressLabel: {
    color: '#fdba74',
    fontSize: 13,
    fontWeight: '800',
    marginBottom: 8,
  },
  meetingAddressInput: {
    backgroundColor: '#1e293b',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
    color: '#fff',
    fontSize: 15,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  meetingAddressPickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#1e293b',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
    paddingHorizontal: 14,
    paddingVertical: 13,
  },
  meetingAddressPickerText: {
    flex: 1,
    color: '#f8fafc',
    fontSize: 15,
    fontWeight: '700',
  },
  meetingAddressPickerPlaceholder: {
    color: '#94a3b8',
    fontWeight: '600',
  },
  meetingAddressPickerAction: {
    color: '#fdba74',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  meetingAddressSelectedText: {
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 17,
    marginTop: 8,
  },
  meetingAddressStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 10,
  },
  meetingAddressStatusText: {
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: '600',
  },
  meetingAddressError: {
    color: '#fca5a5',
    fontSize: 12,
    fontWeight: '600',
    marginTop: 8,
  },
  meetingAddressEmptyText: {
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 10,
  },
  meetingSuggestionList: {
    gap: 8,
    paddingTop: 10,
    paddingBottom: 2,
  },
  meetingSuggestionScroll: {
    maxHeight: 190,
    marginTop: 2,
  },
  meetingSuggestionScrollExpanded: {
    maxHeight: '100%',
  },
  meetingSuggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0f172a',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1e293b',
    gap: 10,
    padding: 10,
  },
  meetingSuggestionTextBlock: {
    flex: 1,
  },
  meetingSuggestionTitle: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '800',
  },
  meetingSuggestionSubtitle: {
    color: '#94a3b8',
    fontSize: 12,
    lineHeight: 16,
    marginTop: 3,
  },
  meetingSuggestionMeta: {
    alignItems: 'flex-end',
    minWidth: 70,
  },
  meetingSuggestionType: {
    color: '#fdba74',
    fontSize: 11,
    fontWeight: '800',
  },
  meetingSuggestionDistance: {
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 4,
  },
  meetingHintText: {
    color: '#cbd5e1',
    fontSize: 12,
    lineHeight: 17,
    marginTop: 8,
  },
  mapsActionsSection: {
    marginTop: 10,
    marginBottom: 2,
  },
  mapsActionsTitle: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  mapsActionsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  mapsActionButton: {
    flex: 1,
    backgroundColor: '#111827',
    borderRadius: 14,
    paddingVertical: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#475569',
  },
  mapsActionButtonGoogle: {
    backgroundColor: '#1d4ed8',
    borderColor: '#60a5fa',
  },
  mapsActionButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  progressSection: {
    marginTop: 2,
    marginBottom: 6,
  },
  pedestrianCompassCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0f172a',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#22c55e',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginTop: 4,
    marginBottom: 8,
    gap: 12,
  },
  pedestrianCompassArrowShell: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#052e16',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#22c55e',
  },
  pedestrianCompassArrow: {
    width: 34,
    height: 38,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  pedestrianCompassArrowHead: {
    width: 0,
    height: 0,
    borderLeftWidth: 10,
    borderRightWidth: 10,
    borderBottomWidth: 16,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: '#22c55e',
  },
  pedestrianCompassArrowStem: {
    width: 7,
    height: 21,
    borderRadius: 4,
    backgroundColor: '#22c55e',
    marginTop: -1,
  },
  pedestrianCompassTextBlock: {
    flex: 1,
  },
  pedestrianCompassLabel: {
    color: '#86efac',
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  pedestrianCompassText: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 18,
  },
  liveContactList: {
    borderTopWidth: 1,
    borderTopColor: '#334155',
    marginTop: 6,
    marginBottom: 8,
    paddingTop: 6,
  },
  liveContactRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 5,
  },
  liveContactIdentity: {
    flex: 1,
  },
  liveContactName: {
    color: '#cbd5e1',
    fontSize: 14,
    fontWeight: '600',
  },
  liveContactInviteLabel: {
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: '600',
    marginTop: 2,
  },
  liveContactDistance: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '800',
  },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    gap: 12,
  },
  progressTitleBlock: {
    flex: 1,
  },
  progressLabel: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '600',
  },
  progressContactName: {
    color: '#86efac',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 2,
  },
  progressValue: {
    color: '#86efac',
    fontSize: 14,
    fontWeight: '700',
  },
  progressTrackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  progressTrack: {
    flex: 1,
    height: 10,
    backgroundColor: '#334155',
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#22c55e',
    borderRadius: 999,
    minWidth: 0,
  },
  liveDistancePill: {
    minWidth: 76,
    backgroundColor: '#0f172a',
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#22c55e',
    paddingHorizontal: 12,
    paddingVertical: 7,
    alignItems: 'center',
  },
  liveDistancePillText: {
    color: '#f8fafc',
    fontSize: 15,
    fontWeight: '800',
  },
  liveDistancePillEmpty: {
    color: '#94a3b8',
  },
  etaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0f172a',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#334155',
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  etaMetric: {
    flex: 1,
  },
  etaLabel: {
    color: '#94a3b8',
    fontSize: 11,
    fontWeight: '800',
    marginBottom: 3,
    textTransform: 'uppercase',
  },
  etaValue: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '800',
  },
  etaDivider: {
    width: 1,
    height: 30,
    backgroundColor: '#334155',
    marginHorizontal: 10,
  },
  floatingToggleButton: {
    position: 'absolute',
    right: 4,
    bottom: -10,
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: '#0f172a',
    borderWidth: 2,
    borderColor: '#22c55e',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.22,
    shadowRadius: 8,
    shadowOffset: {
      width: 0,
      height: 3,
    },
    elevation: 6,
  },
  floatingToggleButtonText: {
    color: '#f8fafc',
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 28,
  },
  nextStepSection: {
    marginTop: 12,
    padding: 12,
    backgroundColor: '#0f172a',
    borderRadius: 14,
  },
  nextStepTitle: {
    color: '#86efac',
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  nextStepText: {
    color: '#f8fafc',
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  nextStepMeta: {
    color: '#cbd5e1',
    fontSize: 13,
  },
  routeSheetSection: {
    marginTop: 12,
  },
  routeSheetTitle: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  routeSheetScroll: {
    maxHeight: 190,
  },
  routeSheetContent: {
    gap: 8,
  },
  routeStepItem: {
    backgroundColor: '#0f172a',
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: '#1e293b',
  },
  routeStepItemActive: {
    borderColor: '#22c55e',
  },
  routeStepHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  routeStepIndex: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: '#334155',
    color: '#f8fafc',
    textAlign: 'center',
    lineHeight: 26,
    fontSize: 12,
    fontWeight: '700',
  },
  routeStepIndexDone: {
    backgroundColor: '#14532d',
  },
  routeStepIndexActive: {
    backgroundColor: '#22c55e',
    color: '#0f172a',
  },
  routeStepBody: {
    flex: 1,
  },
  routeStepInstruction: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  routeStepInstructionDone: {
    color: '#94a3b8',
  },
  routeStepMeta: {
    color: '#94a3b8',
    fontSize: 12,
  },
  signalToast: {
    position: 'absolute',
    top: 58,
    left: 20,
    right: 20,
    zIndex: 2,
    backgroundColor: 'rgba(15, 23, 42, 0.92)',
    borderWidth: 1,
    borderColor: '#22c55e',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  signalToastText: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  socketStatusCard: {
    backgroundColor: '#111827',
    borderWidth: 1,
    borderColor: '#334155',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 14,
  },
  socketStatusFloating: {
    position: 'absolute',
    left: 20,
    right: 20,
    zIndex: 2,
    backgroundColor: 'rgba(15, 23, 42, 0.92)',
    borderWidth: 1,
    borderColor: '#334155',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  socketStatusHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  socketStatusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  socketStatusDotOnline: {
    backgroundColor: '#22c55e',
  },
  socketStatusDotOffline: {
    backgroundColor: '#ef4444',
  },
  socketStatusLabel: {
    color: '#cbd5e1',
    fontSize: 13,
    fontWeight: '600',
    marginRight: 8,
  },
  socketStatusValue: {
    fontSize: 13,
    fontWeight: '700',
  },
  socketStatusValueOnline: {
    color: '#86efac',
  },
  socketStatusValueOffline: {
    color: '#fca5a5',
  },
  socketStatusError: {
    color: '#fca5a5',
    fontSize: 12,
    fontWeight: '600',
    marginTop: 6,
  },
  statusMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  statusMetaLabel: {
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: '600',
    marginRight: 8,
  },
  statusMetaValue: {
    fontSize: 12,
    fontWeight: '700',
  },
  mapEngineValuePreferred: {
    color: '#93c5fd',
  },
  mapEngineValueFallback: {
    color: '#fdba74',
  },
  sessionToast: {
    position: 'absolute',
    top: 112,
    left: 20,
    right: 20,
    zIndex: 2,
    backgroundColor: 'rgba(15, 23, 42, 0.92)',
    borderWidth: 1,
    borderColor: '#22c55e',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  sessionToastWithSignal: {
    top: 124,
  },
  sessionToastText: {
    color: '#f8fafc',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  signalSection: {
    marginTop: 10,
  },
  signalTitle: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  signalRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  signalButton: {
    backgroundColor: '#334155',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  signalButtonText: {
    color: '#f8fafc',
    fontSize: 13,
    fontWeight: '600',
  },
  activitySection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#334155',
  },
  activityTitle: {
    color: '#e2e8f0',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
  },
  activityItem: {
    color: '#cbd5e1',
    fontSize: 13,
    marginBottom: 4,
  },
  stopButton: {
    marginTop: 12,
    backgroundColor: '#ef4444',
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
});
